/**
 * Youswim Find-Your-Fit Quiz — engine
 * -----------------------------------
 * Phase B: real Storefront API integration.
 * - Catalogue + filters + quiz_style/quiz_coverage metaobjects fetched on modal open
 * - Falls back to mock data when token isn't configured (dev convenience)
 * - Filter relaxation safety net at completion (colour → style → coverage)
 * - Loading + error states wired
 * - Silhouette images from theme settings (image_picker)
 * - Klaviyo edge-case POST when company ID + list ID configured
 *
 * URL builder uses REPEATED parameters (spec §4.5).
 * Triangle (Zera) expands to Zera + Mini Zera in the URL filter.
 */
(function () {
  'use strict';

  if (window.__youswimQuizInit) return;
  window.__youswimQuizInit = true;

  // ============================================================
  // ANALYTICS — dataLayer.push() + gtag() (GTM + GA4)
  // Events fire to both for compatibility with whichever channel is
  // installed. Silent on failure — analytics never blocks the quiz.
  // ============================================================
  function track(eventName, props) {
    props = props || {};
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, props));
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, props);
      }
    } catch (e) {
      // silent
    }
  }

  // ============================================================
  // CONFIG — populated by /sections/quiz-modal.liquid
  // ============================================================
  const SECTION_CONFIG = window.YOUSWIM_QUIZ_CONFIG || {};

  const CONFIG = {
    shopDomain:               SECTION_CONFIG.shopDomain || (window.Shopify && window.Shopify.shop) || '',
    storefrontToken:          SECTION_CONFIG.storefrontToken || '',
    apiVersion:               SECTION_CONFIG.apiVersion || '2024-10',
    catalogueHandle:          SECTION_CONFIG.catalogueHandle || 'all-swimsuits',
    // Collections that, when the quiz is launched from their page, become
    // the catalogue for that run (counts, options, narrative, results).
    // Used for sale events — e.g. ['sale']. Comma-separated theme setting.
    contextCatalogueHandles:  String(SECTION_CONFIG.contextCatalogueHandles || '')
      .split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean),
    fallbackCollectionHandle: SECTION_CONFIG.fallbackCollectionHandle || 'discover-youswim-fit-quiz',
    klaviyoCompanyId:         (SECTION_CONFIG.klaviyo && SECTION_CONFIG.klaviyo.companyId) || '',
    klaviyoListId:            (SECTION_CONFIG.klaviyo && SECTION_CONFIG.klaviyo.listId) || '',
    // Optional dedicated list for quiz completers (separate from edge-case
    // waitlist). If blank, completer subscribes go to klaviyoListId.
    klaviyoListIdCompleter:   (SECTION_CONFIG.klaviyo && SECTION_CONFIG.klaviyo.listIdCompleter) || '',
    silhouetteImages:         SECTION_CONFIG.silhouetteImages || { onePiece: null, bikini: null, dressOrRomper: null },
    enableColourQuestion:     SECTION_CONFIG.enableColourQuestion !== false, // default true if unset
    // Per-store metafield key overrides (custom.<key>). Defaults match the
    // canonical Youswim USD setup; GBP overrides 'cut' → 'coverage'.
    metafieldKeys: {
      cut:   (SECTION_CONFIG.metafieldKeys && SECTION_CONFIG.metafieldKeys.cut)   || 'cut',
      style: (SECTION_CONFIG.metafieldKeys && SECTION_CONFIG.metafieldKeys.style) || 'style',
      type:  (SECTION_CONFIG.metafieldKeys && SECTION_CONFIG.metafieldKeys.type)  || 'type',
    },
  };

  const INTRO = Object.assign({
    eyebrow: 'Find your fit',
    headline: 'Swimsuit sizing was <em>broken</em>, so we fixed it.',
    body: 'We created a magically flexible swim fabric that replaces a dozen traditional sizes with two adaptable ones.',
    prompt: 'Let’s find your new favourite swimsuit.',
    buttonLabel: 'Start',
  }, SECTION_CONFIG.intro || {});

  // Optional first-name capture. When enabled, the quiz inserts a
  // dedicated screen between the intro and Q1: customer is asked their
  // first name, optionally enters it, hits Next. If they entered a name,
  // a brief confirmation message ("We're glad you're here, Julian.")
  // shows for `confirmationDurationMs` before advancing to Q1.
  // The captured name is then used to personalise question titles,
  // the email step headline, and (if merchant opts in) the summary
  // headline via the {name} token. Disabled = no name step at all.
  const NAME_CAPTURE = Object.assign({
    enabled:                 true,
    heading:                 'First — what should we call you?',
    placeholder:             'First name',
    buttonLabel:             'Next',
    skipLabel:               'Skip',
    confirmationMsg:         'We’re glad you’re here{name}.',
    confirmationDurationMs:  2000,
  }, SECTION_CONFIG.nameCapture || {});

  // Per-question titles (theme-editable, support {name} token). Defaults
  // mirror the prior hardcoded copy, except the size question which uses
  // the {name} token to open the conversation on a personal beat.
  const QUESTION_TITLES = Object.assign({
    size:       'What’s your usual dress size{name}?',
    silhouette: 'What are you looking for?',
    style:      'What’s your preferred style?',
    coverage:   'How much coverage do you like?',
    colour:     'Any colour preferences?',
  }, SECTION_CONFIG.questionTitles || {});

  // Email capture step (post-Q-last, pre-summary).
  const EMAIL_CAPTURE = Object.assign({
    enabled:     true,
    eyebrow:     'Almost there',
    headline:    'What’s your email{name}?',
    body:        'Be first to know about new drops and restocks.',
    placeholder: 'your@email.com',
    buttonLabel: 'Continue',
    skipLabel:   'Skip',
    successMsg:  'Thanks — you’re in.',
  }, SECTION_CONFIG.emailCapture || {});

  // PDP badge — context-adaptive chip shown above the mix-match selector
  // (or product title). One of three states based on customer journey:
  //   stateA — pre-quiz visitor → "Take the fit quiz", opens modal
  //   stateB — completed + on a recommended product → "Recommended for you" / hover "Back to quiz results"
  //   stateC — completed + on a non-match product → "Back to my quiz results"
  // Each state has its own copy + colours, fully editable in theme.
  const PDP_BADGE = Object.assign({
    enabled:   true,
    swimOnly:  true,
    swimTypes: 'Swimwear, Bodywear',
    stateA: { copy: 'Take the fit quiz',          bg: '#E4EDEB', text: '#276254' },
    stateB: {
      defaultCopy: 'Recommended for you',
      hoverCopy:   '← Back to my quiz results',
      bg: '#F2EFEB', text: '#403633',
    },
    stateC: { copy: '← Back to my quiz results', bg: '#FBFBFA', text: '#7D726D' },
  }, SECTION_CONFIG.pdpBadge || {});

  // Set when the rendering page is a /products/* PDP. Used for the
  // swim-only check (without needing a fetch to determine product type).
  const CURRENT_PRODUCT = SECTION_CONFIG.currentProduct || { handle: null, type: null };

  const EDGE = Object.assign({
    smallTitle: 'Sizing in progress',
    smallBody:  'We currently make swimwear in US 2–24. We’re working on expanding into smaller sizing — pop your email in and we’ll let you know as soon as it launches.',
    largeTitle: 'Sizing in progress',
    largeBody:  'We currently make swimwear in US 2–24. We’re working on expanding into more inclusive sizing — pop your email in and we’ll let you know as soon as it launches.',
  }, SECTION_CONFIG.edge || {});

  const SUMMARY = Object.assign({
    headline: 'Your <em>matches</em> are ready',
    loadingMessages: ['Adapting to fit you…', 'Solving swimsuit shopping…', 'Making your day…'],
    loadingMessageDurationMs: 1000,
  }, SECTION_CONFIG.summary || {});

  // ============================================================
  // COLOUR VALUE → GROUP MAPPING (from theme editor textarea)
  // Format: one line per group, "GroupName: value1, value2, value3".
  // We can't read Shopify Search & Discovery's grouping config via Storefront
  // API, so the merchant maintains this mirror in the theme editor.
  // ============================================================
  function parseColourGroupsMapping(text) {
    const result = {}; // value → groupLabel
    if (!text) return result;
    String(text).split(/\r?\n/).forEach(function (line) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.charAt(0) === '#') return;
      const colonIdx = trimmed.indexOf(':');
      if (colonIdx === -1) return;
      const groupLabel = trimmed.slice(0, colonIdx).trim();
      const valuesStr  = trimmed.slice(colonIdx + 1).trim();
      if (!groupLabel || !valuesStr) return;
      valuesStr.split(',').forEach(function (raw) {
        const value = raw.trim();
        if (value) result[value] = groupLabel;
      });
    });
    return result;
  }
  // value → groupLabel (resolved to GID once colourGroups are loaded)
  const COLOUR_VALUE_TO_GROUP_LABEL = parseColourGroupsMapping(SECTION_CONFIG.colourGroupsMapping || '');

  // ============================================================
  // STATIC DATA
  // ============================================================
  const ONE_PIECE_COVERAGE_VALUE = 'One-Piece';  // exact metafield value used in custom.cut
  // 'Brazillian' (store's spelling) added 2026-07: Brazilian-cut bottoms in
  // the EOS sale carry it as their cut value — without it here they never
  // count as bikini matches.
  const BIKINI_COVERAGE_VALUES = ['Standard Waist', 'High Waist', 'Tie Sides', 'Shorts', 'Cheeky', 'Brazillian'];
  // Dress / romper path — distinguished by the product-level metafield
  // `custom.type` rather than by cut. Includes Youswim's swim dresses and
  // rompers. Selecting this on Q2 skips Q3 (style), Q4 (coverage), Q5 (colour).
  const BODYWEAR_TYPE_VALUE = 'Bodywear';

  // ============================================================
  // SIZE SYSTEMS — display values per region with US-normalized
  // mapping. The Storefront API filter is always queried by
  // Stretch I / Stretch II (system-agnostic) — size systems are
  // purely a display layer that maps reported size → stretch range.
  // For reporting: state.answers.size is always stored as the
  // US-normalized number (e.g. "8"), while state.answers.sizeReported
  // captures what the customer actually picked (e.g. "UK12").
  // Offsets: UK/AUS = US + 4. EU = US + 32. Canadian = US.
  // ============================================================
  const SIZE_SYSTEMS = {
    us_ca: {
      id: 'us_ca',
      label: 'US / Canadian',
      pillLabel: 'US/CA',
      prefix: 'US',
      offset: 0,
      sizes: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24],
      edgeSmallLabel: 'Smaller than US 2',
      edgeLargeLabel: 'Larger than US 24'
    },
    uk_aus: {
      id: 'uk_aus',
      label: 'UK / AUS',
      pillLabel: 'UK/AUS',
      prefix: 'UK',
      offset: 4,
      sizes: [6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28],
      edgeSmallLabel: 'Smaller than UK/AUS 6',
      edgeLargeLabel: 'Larger than UK/AUS 28'
    },
    eu: {
      id: 'eu',
      label: 'EU',
      pillLabel: 'EU',
      prefix: 'EU',
      offset: 32,
      sizes: [34, 36, 38, 40, 42, 44, 46, 48, 50, 52, 54, 56],
      edgeSmallLabel: 'Smaller than EU 34',
      edgeLargeLabel: 'Larger than EU 56'
    }
  };

  // ISO country code → preferred size system. Falls back to the
  // theme-editor "Default size system" when no match.
  const COUNTRY_TO_SIZE_SYSTEM = {
    GB: 'uk_aus', IE: 'uk_aus', AU: 'uk_aus', NZ: 'uk_aus',
    US: 'us_ca', CA: 'us_ca',
    // EU + EEA + Switzerland
    AT: 'eu', BE: 'eu', BG: 'eu', HR: 'eu', CY: 'eu', CZ: 'eu',
    DK: 'eu', EE: 'eu', FI: 'eu', FR: 'eu', DE: 'eu', GR: 'eu',
    HU: 'eu', IS: 'eu', IT: 'eu', LV: 'eu', LI: 'eu', LT: 'eu',
    LU: 'eu', MT: 'eu', NL: 'eu', NO: 'eu', PL: 'eu', PT: 'eu',
    RO: 'eu', SK: 'eu', SI: 'eu', ES: 'eu', SE: 'eu', CH: 'eu'
  };

  function getEnabledSizeSystems() {
    const cfg = SECTION_CONFIG.sizeSystems || {};
    const enabled = [];
    if (cfg.us_caEnabled  !== false) enabled.push('us_ca');
    if (cfg.uk_ausEnabled === true)  enabled.push('uk_aus');
    if (cfg.euEnabled     === true)  enabled.push('eu');
    // Always guarantee at least one — fall back to us_ca if merchant
    // accidentally disabled everything.
    if (!enabled.length) enabled.push('us_ca');
    return enabled;
  }

  function detectInitialSizeSystem() {
    const cfg = SECTION_CONFIG.sizeSystems || {};
    const enabled = getEnabledSizeSystems();
    const fallback = (cfg.default && enabled.indexOf(cfg.default) !== -1)
      ? cfg.default
      : enabled[0];
    if (cfg.autoDetect === false) return fallback;
    const country = String(cfg.country || '').toUpperCase();
    const detected = COUNTRY_TO_SIZE_SYSTEM[country];
    if (detected && enabled.indexOf(detected) !== -1) return detected;
    return fallback;
  }

  // Convert any reported size (display value within a given system) to
  // its US-normalized numeric equivalent. Returns null if size isn't valid.
  function reportedSizeToUS(displaySize, systemId) {
    const n = parseInt(displaySize, 10);
    if (isNaN(n)) return null;
    const sys = SIZE_SYSTEMS[systemId] || SIZE_SYSTEMS.us_ca;
    return n - sys.offset;
  }

  // Convert a US-normalized numeric size to its display in another system.
  // Returns null if outside the available range.
  function usSizeToDisplay(usSize, systemId) {
    const sys = SIZE_SYSTEMS[systemId] || SIZE_SYSTEMS.us_ca;
    const display = usSize + sys.offset;
    return sys.sizes.indexOf(display) !== -1 ? String(display) : null;
  }

  function formatReportedSize(displaySize, systemId) {
    const sys = SIZE_SYSTEMS[systemId] || SIZE_SYSTEMS.us_ca;
    return sys.prefix + displaySize;
  }

  // Quiz-option → product-style expansions. When a customer picks a quiz
  // style option whose internal value appears as a key here, the filter
  // URL (and local match count) expand to include ALL listed style values.
  //   Triangle → Zera + Mini Zera
  //   Bandeau (labeled "Bandeau", internal "Poise") → Poise + Juno
  const STYLE_URL_EXPANSIONS = {
    'Zera':  ['Zera', 'Mini Zera'],
    'Poise': ['Poise', 'Juno'],
  };

  // Coverage (Q4) expansions — same mechanism as styles. Selecting the
  // quiz's Cheeky option matches/filters Brazilian-cut products too, so
  // Brazilian doesn't need its own Q4 option, icon, or metaobject.
  // 'Bazillian' is a known typo'd cut value on one USD product — included
  // so it matches while the product data awaits fixing (harmless in filter
  // URLs: unknown values simply match nothing).
  const COVERAGE_VALUE_EXPANSIONS = {
    'Cheeky': ['Cheeky', 'Brazillian', 'Bazillian'],
  };

  // Best-effort label → swatch hex (Storefront API doesn't return swatch colours
  // for filter setting groups in all versions). If a label isn't in this map
  // a neutral grey ring is used.
  const COLOUR_SWATCH_HEX = {
    'black':    '#1a1a1a', 'blacks':   '#1a1a1a',
    'white':    '#f5f1ec', 'whites':   '#f5f1ec',
    'blue':     '#2c4a6b', 'blues':    '#2c4a6b',
    'navy':     '#1a2a44',
    'brown':    '#7a5a3a', 'browns':   '#7a5a3a',
    'tan':      '#c9a87a',
    'grey':     '#8a8a8a', 'greys':    '#8a8a8a',
    'gray':     '#8a8a8a', 'grays':    '#8a8a8a',
    'green':    '#4a5d3a', 'greens':   '#4a5d3a',
    'olive':    '#6a6a3a',
    'neutral':  '#c2b39a', 'neutrals': '#c2b39a',
    'beige':    '#d8c8a8',
    'pink':     '#d8a8a8', 'pinks':    '#d8a8a8',
    'purple':   '#6b4a6b', 'purples':  '#6b4a6b',
    'red':      '#a83838', 'reds':     '#a83838',
    'yellow':   '#d8c878', 'yellows':  '#d8c878',
    'orange':   '#d89858', 'oranges':  '#d89858',
    'multi':    'conic-gradient(from 0deg, #d8a8a8, #d8c878, #4a5d3a, #2c4a6b, #6b4a6b, #d8a8a8)',
  };

  function getSwatchStyle(label) {
    const key = String(label || '').toLowerCase().trim();
    const v = COLOUR_SWATCH_HEX[key];
    if (!v) return 'background:#cfc8bd;';
    if (v.indexOf('gradient') !== -1) return 'background:' + v + ';';
    return 'background:' + v + ';';
  }

  // ============================================================
  // STATE
  // ============================================================
  const STORAGE_KEY = 'youswim_quiz_v1';

  function storageAvailable() {
    try {
      const t = '__youswim_quiz_test__';
      localStorage.setItem(t, '1');
      localStorage.removeItem(t);
      return true;
    } catch (e) { return false; }
  }

  function saveQuizState(payload) {
    if (!storageAvailable()) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) {}
  }
  function loadQuizState() {
    if (!storageAvailable()) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function clearQuizState() {
    if (!storageAvailable()) return;
    // NOTE: only clears the per-attempt key. We intentionally do NOT clear
    // EMAIL_CAPTURED_KEY here — once a customer has submitted their email,
    // we never re-prompt them (even across restarts).
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // Customer-level permanent flag: set on first successful email submit,
  // never cleared by Restart quiz. Used to suppress the email step forever
  // for customers who already opted in.
  const EMAIL_CAPTURED_KEY = 'youswim_quiz_email_captured';
  function markEmailCaptured() {
    if (!storageAvailable()) return;
    try { localStorage.setItem(EMAIL_CAPTURED_KEY, '1'); } catch (e) {}
  }
  function hasEmailEverBeenCaptured() {
    if (!storageAvailable()) return false;
    try { return localStorage.getItem(EMAIL_CAPTURED_KEY) === '1'; }
    catch (e) { return false; }
  }

  // Trim + cap length + capitalise first letter. Empty input → empty string.
  function capitaliseName(raw) {
    const trimmed = String(raw || '').trim().slice(0, 60);
    if (!trimmed) return '';
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  // Replace the {name} token in any copy string:
  //   - if firstName is set: "{name}" → ", FirstName" (leading comma + space)
  //   - if firstName is empty: "{name}" → "" (empty, no orphan comma)
  // Customers should never see the literal token under either branch.
  function personaliseCopy(template, firstName) {
    const replacement = firstName ? ', ' + firstName : '';
    return String(template || '').replace(/\{name\}/g, replacement);
  }

  const state = {
    onIntro: true,
    completed: false,     // true once the summary screen has been reached
    currentIndex: 0,
    answers: {},
    relaxed: [],          // names of filters relaxed at completion
    openSource: null,     // 'cta' | 'deeplink' | 'debug'
    abandonFired: false,  // de-dupe the quiz_abandoned event
    lastRenderedQuestionId: null,  // suppress entry animation on same-question re-renders
    sizeSystem: 'us_ca',  // 'us_ca' | 'uk_aus' | 'eu' — initialized on quiz open
    firstName: '',                  // captured on dedicated name step, used for personalisation
    onNameStep: false,              // dedicated screen between intro and Q1 — only shown when NAME_CAPTURE.enabled
    emailInterstitialShown: false,  // per-attempt — true once submit OR skip happened
    // Catalogue (populated from API)
    variants: [],
    colourGroups: [],     // [{ gid, label, count }]
    styleOptions: [],     // [{ internal, label, diagramUrl, show, bikiniOnlyOverride, order }]
    coverageOptions: [],
    // Fetch lifecycle
    fetching: false,
    fetched: false,
    fetchError: null,
    usingMock: false,
    // Resolved colour value → group GID lookup (built when colourGroups are
    // known and the user-maintained mapping is present).
    colourValueToGid: null,
    // DOM refs
    modalEl: null, bodyEl: null, progressEl: null, stepEl: null,
    backEl: null, continueEl: null, lastFocusEl: null,
    systemStripEl: null, systemPillsEl: null,
  };

  // ============================================================
  // MOCK DATA — used when storefront token isn't configured
  // ============================================================
  function loadMockData() {
    const groups = [
      { gid: 'gid://shopify/FilterSettingGroup/mock-blacks',   label: 'Blacks' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-whites',   label: 'Whites' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-blues',    label: 'Blues' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-greens',   label: 'Greens' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-browns',   label: 'Browns' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-pinks',    label: 'Pinks' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-reds',     label: 'Reds' },
      { gid: 'gid://shopify/FilterSettingGroup/mock-neutrals', label: 'Neutrals' },
    ];
    const styles = [
      { internal: 'Eva',    label: 'Low V-neck',    diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 10 },
      { internal: 'Poise',  label: 'Bandeau',       diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 20 },
      { internal: 'Aplomb', label: 'Classic scoop', diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 30 },
      { internal: 'Zera',   label: 'Triangle',      diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 40 },
      { internal: 'Verve',  label: 'Long top',      diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 50 },
      { internal: 'Una',    label: 'One-shoulder',  diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 60 },
      { internal: 'Elan',   label: 'Racer back',    diagramUrl: null, show: true, bikiniOnlyOverride: false, order: 70 },
    ];
    const coverages = [
      { internal: 'Standard Waist', label: 'Classic brief',     diagramUrl: null, show: true, order: 10 },
      { internal: 'High Waist',     label: 'Classic, but high', diagramUrl: null, show: true, order: 20 },
      { internal: 'Tie Sides',      label: 'Triangle',          diagramUrl: null, show: true, order: 30 },
      { internal: 'Shorts',         label: 'Shorts',            diagramUrl: null, show: true, order: 40 },
      { internal: 'Cheeky',         label: 'Cheeky',            diagramUrl: null, show: true, order: 50 },
    ];

    const products = [
      { handle: 'eva-onepiece', title: 'Eva',           style: 'Eva',    coverage: 'one-piece' },
      { handle: 'eva-bikini',   title: 'Eva Set',       style: 'Eva',    coverage: 'Cheeky' },
      { handle: 'aplomb',       title: 'Aplomb',        style: 'Aplomb', coverage: 'one-piece' },
      { handle: 'aplomb-set',   title: 'Aplomb Set',    style: 'Aplomb', coverage: 'Standard Waist' },
      { handle: 'poise',        title: 'Poise',         style: 'Poise',  coverage: 'High Waist' },
      { handle: 'zera',         title: 'Zera',          style: 'Zera',   coverage: 'Tie Sides' },
      { handle: 'mini-zera',    title: 'Mini Zera',     style: 'Mini Zera', coverage: 'Tie Sides' },
      { handle: 'verve',        title: 'Verve',         style: 'Verve',  coverage: 'one-piece' },
      { handle: 'una',          title: 'Una',           style: 'Una',    coverage: 'one-piece' },
      { handle: 'elan',         title: 'Elan',          style: 'Elan',   coverage: 'one-piece' },
      { handle: 'shorts-set',   title: 'Marlow Shorts', style: 'Aplomb', coverage: 'Shorts' },
    ];
    const variants = [];
    products.forEach(function (p, pi) {
      const colourSlice = groups.slice(0, 4 + (pi % 5));
      colourSlice.forEach(function (c) {
        ['Stretch I', 'Stretch II'].forEach(function (s) {
          variants.push({
            productHandle: p.handle, productTitle: p.title,
            style: p.style, coverage: p.coverage, size: s,
            colourGid: c.gid, colourLabel: c.label,
          });
        });
      });
    });

    state.variants = variants;
    state.colourGroups = groups;
    state.styleOptions = styles;
    state.coverageOptions = coverages;
    state.usingMock = true;
    state.fetched = true;
  }

  // ============================================================
  // STOREFRONT API
  // ============================================================
  const CATALOGUE_QUERY = (
    'query QuizCatalogue($handle: String!) {' +
    '  collection(handle: $handle) {' +
    '    handle' +
    '    products(first: 250) {' +
    '      filters { id label type values { id label count input } }' +
    '      edges { node {' +
    '        id handle title tags' +
    '        options { name values }' +
    '        style:    metafield(namespace: "custom", key: "' + CONFIG.metafieldKeys.style + '") { value }' +
    '        coverage: metafield(namespace: "custom", key: "' + CONFIG.metafieldKeys.cut   + '") { value }' +
    '        productType: metafield(namespace: "custom", key: "' + CONFIG.metafieldKeys.type  + '") { value }' +
    '        variants(first: 50) { edges { node {' +
    '          id title availableForSale' +
    '          selectedOptions { name value }' +
    '          price { amount currencyCode }' +
    '          image { url altText }' +
    '        } } }' +
    '        featuredImage { url altText }' +
    '      } }' +
    '    }' +
    '  }' +
    '  quizStyles: metaobjects(type: "quiz_style", first: 50) {' +
    '    edges { node { id type fields { key value reference { ... on MediaImage { image { url altText } } } } } }' +
    '  }' +
    '  quizCoverages: metaobjects(type: "quiz_coverage", first: 50) {' +
    '    edges { node { id type fields { key value reference { ... on MediaImage { image { url altText } } } } } }' +
    '  }' +
    '}'
  );

  function storefrontFetch(query, variables) {
    if (!CONFIG.shopDomain || !CONFIG.storefrontToken) {
      return Promise.reject(new Error('no-token'));
    }
    const url = 'https://' + CONFIG.shopDomain + '/api/' + CONFIG.apiVersion + '/graphql.json';
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken,
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    }).then(function (res) {
      if (!res.ok) throw new Error('http-' + res.status);
      return res.json();
    }).then(function (json) {
      if (json.errors && json.errors.length) throw new Error(json.errors[0].message || 'graphql-error');
      return json.data;
    });
  }

  function fetchCatalogue() {
    return storefrontFetch(CATALOGUE_QUERY, { handle: getEffectiveCatalogueHandle() });
  }

  // -----------------------------------------------------------------------
  // COLOUR AVAILABILITY:
  // The Storefront API doesn't expose which variant colour values belong to
  // which Search & Discovery filter setting group, and its product-level
  // filter logic can't answer "which group does this size+style+coverage
  // combo have purchasable variants in?". So the merchant maintains a small
  // value→group mapping via theme editor (mirroring their Search & Discovery
  // config) and we compute per-group availability locally from state.variants.
  // -----------------------------------------------------------------------
  function buildColourValueToGidLookup() {
    state.colourValueToGid = null;
    if (!state.colourGroups || !state.colourGroups.length) return;
    if (!Object.keys(COLOUR_VALUE_TO_GROUP_LABEL).length) return; // no mapping configured
    const labelToGid = {};
    state.colourGroups.forEach(function (g) {
      labelToGid[(g.label || '').toLowerCase()] = g.gid;
    });
    const lookup = {};
    Object.keys(COLOUR_VALUE_TO_GROUP_LABEL).forEach(function (value) {
      const label = COLOUR_VALUE_TO_GROUP_LABEL[value];
      const gid = labelToGid[label.toLowerCase()];
      if (gid) lookup[value] = gid;
    });
    state.colourValueToGid = lookup;
  }

  // Per-group product counts computed CLIENT-SIDE using state.variants
  // (already filtered to available variants in the parse step) + the
  // value→GID mapping. Sets `.count` on each state.colourGroups entry.
  // If no mapping is configured, leaves the groups alone (all selectable).
  function refreshColourCounts() {
    if (!state.colourValueToGid) return Promise.resolve();

    const stretchRange = dressSizeToStretch(state.answers.size);
    const isOP = state.answers.silhouette === 'one-piece';
    const isBK = state.answers.silhouette === 'bikini';
    const styleAnswer = state.answers.style || [];
    const expandedStyles = styleAnswer.length ? expandStyleValues(styleAnswer) : null;
    const coverageAnswer = state.answers.coverage || [];
    const expandedCoverage = coverageAnswer.length ? expandCoverageValues(coverageAnswer) : null;

    const matching = state.variants.filter(function (v) {
      if (stretchRange && v.size !== stretchRange) return false;
      if (isOP && !isOnePiece(v)) return false;
      if (isBK && !isBikini(v)) return false;
      if (expandedStyles && expandedStyles.indexOf(v.style) === -1) return false;
      if (expandedCoverage && expandedCoverage.indexOf(v.coverage) === -1) return false;
      return true;
    });

    const productsByGroup = {};
    matching.forEach(function (v) {
      const gid = state.colourValueToGid[v.colourValue];
      if (!gid) return; // value not in mapping — won't count toward any group
      if (!productsByGroup[gid]) productsByGroup[gid] = new Set();
      productsByGroup[gid].add(v.productHandle);
    });

    state.colourGroups = state.colourGroups.map(function (g) {
      return Object.assign({}, g, { count: (productsByGroup[g.gid] || new Set()).size });
    });
    return Promise.resolve();
  }
  // -----------------------------------------------------------------------
  // Extract the canonical filter value (a GID for FilterSettingGroup) from a
  // Storefront API filter `values` entry. The `id` field comes back in a
  // dotted/dashed form that ISN'T URL-safe (`filter.v.option.color.gid-shopify-
  // filtersettinggroup-172359996`); the canonical value lives inside `input`,
  // a JSON-encoded string. Fall back to id-decoding if input is missing.
  function extractFilterGid(v) {
    if (v.input) {
      try {
        const parsed = typeof v.input === 'string' ? JSON.parse(v.input) : v.input;
        if (parsed && parsed.variantOption && parsed.variantOption.value) return parsed.variantOption.value;
        if (parsed && parsed.productMetafield && parsed.productMetafield.value) return parsed.productMetafield.value;
      } catch (e) { /* fall through */ }
    }
    if (v.id) {
      const m = v.id.match(/gid-shopify-filtersettinggroup-(\d+)$/i);
      if (m) return 'gid://shopify/FilterSettingGroup/' + m[1];
    }
    return null;
  }

  function parseMetaobject(node) {
    const fields = {};
    (node.fields || []).forEach(function (f) {
      if (f.reference && f.reference.image && f.reference.image.url) {
        fields[f.key] = f.reference.image.url;
      } else {
        fields[f.key] = f.value;
      }
    });
    return {
      id:           node.id,
      internal:     fields.internal_value || '',
      label:        fields.display_label || fields.internal_value || '',
      diagramUrl:   fields.diagram || null,
      // Optional: separate drawing used on Q3 when the customer picked
      // one-piece on Q2. Falls back to `diagram` if not uploaded.
      diagramOnePieceUrl: fields.diagram_one_piece || null,
      show:        (fields.show_in_quiz || '').toLowerCase() !== 'false',
      bikiniOnlyOverride: (fields.bikini_only_override || '').toLowerCase() === 'true',
      order:        parseInt(fields.display_order, 10) || 0,
    };
  }

  function parseCatalogueResponse(data) {
    if (!data || !data.collection) throw new Error('no-collection');
    const productsConn = data.collection.products;
    const productEdges = (productsConn && productsConn.edges) || [];
    if (!productEdges.length) throw new Error('empty-collection');

    // Flat variant list (one per variant) with product metafields merged.
    const variants = [];
    productEdges.forEach(function (edge) {
      const p = edge.node;
      const styleVal    = p.style       ? p.style.value       : null;
      const coverageVal = p.coverage    ? p.coverage.value    : null;
      const typeVal     = p.productType ? p.productType.value : null;
      const variantEdges = (p.variants && p.variants.edges) || [];
      variantEdges.forEach(function (ve) {
        const v = ve.node;
        if (!v.availableForSale) return;
        let sizeVal = null, colourVal = null;
        (v.selectedOptions || []).forEach(function (o) {
          const n = (o.name || '').toLowerCase();
          if (n === 'size') sizeVal = o.value;
          else if (n === 'color' || n === 'colour') colourVal = o.value;
        });
        variants.push({
          productHandle: p.handle, productTitle: p.title,
          productId: p.id, variantId: v.id,
          style: styleVal, coverage: coverageVal, size: sizeVal,
          productType: typeVal,
          colourValue: colourVal,
          price: v.price ? v.price.amount : null,
          image: (v.image && v.image.url) || (p.featuredImage && p.featuredImage.url) || null,
        });
      });
    });

    // Colour groups from filter definitions
    const filters = (productsConn && productsConn.filters) || [];
    const colourFilter = filters.filter(function (f) {
      const id = (f.id || '').toLowerCase();
      return id.indexOf('color') !== -1 || id.indexOf('colour') !== -1;
    })[0];
    const colourGroups = colourFilter
      ? (colourFilter.values || []).map(function (v) {
          return { gid: extractFilterGid(v), label: v.label, count: v.count };
        }).filter(function (g) { return g.count > 0 && g.gid; })
      : [];

    // Metaobjects → quiz options
    const styleOptions = ((data.quizStyles && data.quizStyles.edges) || [])
      .map(function (e) { return parseMetaobject(e.node); })
      .filter(function (o) { return o.show && o.internal; })
      .sort(function (a, b) { return a.order - b.order || a.label.localeCompare(b.label); });
    const coverageOptions = ((data.quizCoverages && data.quizCoverages.edges) || [])
      .map(function (e) { return parseMetaobject(e.node); })
      .filter(function (o) { return o.show && o.internal; })
      .sort(function (a, b) { return a.order - b.order || a.label.localeCompare(b.label); });

    if (!styleOptions.length || !coverageOptions.length) {
      throw new Error('empty-metaobjects');
    }

    return { variants: variants, colourGroups: colourGroups, styleOptions: styleOptions, coverageOptions: coverageOptions };
  }

  function loadCatalogue() {
    state.fetching = true;
    state.fetchError = null;
    return fetchCatalogue()
      .then(parseCatalogueResponse)
      .then(function (parsed) {
        state.variants = parsed.variants;
        state.colourGroups = parsed.colourGroups;
        state.styleOptions = parsed.styleOptions;
        state.coverageOptions = parsed.coverageOptions;
        state.usingMock = false;
        state.fetched = true;
        state.fetching = false;
        buildColourValueToGidLookup();
        return parsed;
      })
      .catch(function (err) {
        state.fetching = false;
        if (err && err.message === 'no-token') {
          console.warn('[Youswim Quiz] Storefront API token not configured — falling back to mock data. Set the token in Theme editor → Find Your Fit quiz section.');
          loadMockData();
          return null;
        }
        state.fetchError = err && err.message ? err.message : 'unknown';
        console.error('[Youswim Quiz] Catalogue fetch failed:', err);
        track('quiz_fetch_failed', { error_category: state.fetchError });
        throw err;
      });
  }

  function loadCatalogueWithRetry() {
    return loadCatalogue().catch(function () {
      // Silent retry after 800ms
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          loadCatalogue().then(resolve, reject);
        }, 800);
      });
    });
  }

  // ============================================================
  // FILTER LOGIC
  // ============================================================
  function isOnePiece(v)      { return v.coverage === ONE_PIECE_COVERAGE_VALUE; }
  function isBikini(v)        { return BIKINI_COVERAGE_VALUES.indexOf(v.coverage) !== -1; }
  function isDressOrRomper(v) { return v.productType === BODYWEAR_TYPE_VALUE; }

  function dressSizeToStretch(dressSize) {
    const n = parseInt(dressSize, 10);
    if (isNaN(n)) return null;
    if (n >= 2 && n <= 12) return 'Stretch I';
    if (n >= 14 && n <= 24) return 'Stretch II';
    return null;
  }

  function expandStyleValues(values) {
    return values.reduce(function (acc, s) {
      return acc.concat(STYLE_URL_EXPANSIONS[s] || [s]);
    }, []);
  }

  function expandCoverageValues(values) {
    return values.reduce(function (acc, c) {
      return acc.concat(COVERAGE_VALUE_EXPANSIONS[c] || [c]);
    }, []);
  }


  function getMatchingVariants(answers) {
    const stretchRange = answers.size ? dressSizeToStretch(answers.size) : null;
    const expandedStyles = answers.style && answers.style.length
      ? expandStyleValues(answers.style) : null;
    const expandedCoverage = answers.coverage && answers.coverage.length
      ? expandCoverageValues(answers.coverage) : null;
    return state.variants.filter(function (v) {
      if (stretchRange && v.size !== stretchRange) return false;
      if (answers.silhouette === 'one-piece'       && !isOnePiece(v))      return false;
      if (answers.silhouette === 'bikini'          && !isBikini(v))        return false;
      if (answers.silhouette === 'dress-or-romper' && !isDressOrRomper(v)) return false;
      if (expandedStyles && expandedStyles.indexOf(v.style) === -1) return false;
      if (expandedCoverage && expandedCoverage.indexOf(v.coverage) === -1) return false;
      // Colour filtering is approximate in-quiz: we don't have per-variant group
      // membership from the Storefront API. Match is based on the URL filter at
      // collection-page level. In-quiz counts skip the colour axis except in
      // mock mode where we synthesise it.
      if (answers.colour && answers.colour.length && state.usingMock) {
        if (answers.colour.indexOf(v.colourGid) === -1) return false;
      }
      return true;
    });
  }

  function countMatchingProducts(answers) {
    const seen = new Set();
    getMatchingVariants(answers).forEach(function (v) { seen.add(v.productHandle); });
    return seen.size;
  }

  // Returns the subset of state.answers for questions at or before
  // `questionId` in the active flow. Excludes answers from questions
  // LATER in the funnel — important when the customer has navigated back
  // to an earlier question: the badges on this question shouldn't be
  // pre-narrowed by their later (now-changeable) selections.
  function answersUpTo(questionId, includeCurrent) {
    const qs = getActiveQuestions();
    let idx = -1;
    for (let i = 0; i < qs.length; i++) {
      if (qs[i].id === questionId) { idx = i; break; }
    }
    if (idx < 0) return Object.assign({}, state.answers);
    const upper = includeCurrent ? idx : idx - 1;
    const result = {};
    for (let i = 0; i <= upper; i++) {
      const id = qs[i].id;
      if (state.answers[id] !== undefined) result[id] = state.answers[id];
    }
    return result;
  }

  function countForOption(questionKey, optionValue) {
    // Only factor in answers to PRIOR questions, plus the hypothetical
    // for this question's option. Later questions' answers (which the
    // customer may have set on a previous walk-through) are ignored.
    const test = answersUpTo(questionKey, false);
    if (questionKey === 'style' || questionKey === 'coverage' || questionKey === 'colour') {
      test[questionKey] = [optionValue];
    } else {
      test[questionKey] = optionValue;
    }
    return countMatchingProducts(test);
  }

  // ============================================================
  // QUESTIONS CONFIG (built from current state)
  // ============================================================
  function silhouetteIconHTML(which) {
    const url = (CONFIG.silhouetteImages || {})[which];
    if (url) {
      return '<img src="' + url + '" alt="" loading="eager" />';
    }
    // Fallback placeholder
    const letter = which === 'onePiece' ? 'O' : which === 'bikini' ? 'B' : 'D';
    return '<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="14" y="14" width="52" height="52" rx="4" stroke-dasharray="3 3" opacity="0.4"/><text x="40" y="46" text-anchor="middle" font-family="ABCOracleVariable, Georgia, serif" font-size="22" font-style="italic" fill="currentColor" stroke="none" opacity="0.6">' + letter + '</text></svg>';
  }

  function diagramHTMLFromUrl(url, fallbackLetter) {
    if (url) return '<img src="' + url + '" alt="" loading="eager" />';
    return '<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="14" y="14" width="52" height="52" rx="4" stroke-dasharray="3 3" opacity="0.4"/><text x="40" y="46" text-anchor="middle" font-family="ABCOracleVariable, Georgia, serif" font-size="22" font-style="italic" fill="currentColor" stroke="none" opacity="0.6">' + fallbackLetter + '</text></svg>';
  }

  function colourSwatchHTML(label) {
    return '<span class="quiz-option__swatch" style="' + getSwatchStyle(label) + '"></span>';
  }

  function buildQuestions() {
    const isOnePiecePath = state.answers.silhouette === 'one-piece';
    const styleOpts = state.styleOptions
      .filter(function (o) {
        if (isOnePiecePath && o.bikiniOnlyOverride) return false;
        return true;
      })
      .map(function (o) {
        // On the one-piece path, prefer the one-piece-specific diagram if
        // the merchant uploaded one (field `diagram_one_piece` on the
        // quiz_style metaobject); otherwise fall back to the default.
        const url = (isOnePiecePath && o.diagramOnePieceUrl) ? o.diagramOnePieceUrl : o.diagramUrl;
        return { label: o.label, value: o.internal, diagramHTML: diagramHTMLFromUrl(url, (o.label || '?').charAt(0)) };
      });

    const coverageOpts = state.coverageOptions.map(function (o) {
      return { label: o.label, value: o.internal, diagramHTML: diagramHTMLFromUrl(o.diagramUrl, (o.label || '?').charAt(0)) };
    });

    const colourOpts = state.colourGroups.map(function (c) {
      return {
        label: c.label,
        value: c.gid,
        diagramHTML: colourSwatchHTML(c.label),
        precomputedCount: typeof c.count === 'number' ? c.count : null,
      };
    });

    // Build the Q1 size options dynamically based on the active size
    // system (state.sizeSystem). The OPTION VALUE is always the
    // US-normalized number (so all downstream logic — Stretch range,
    // matching, filter URL — stays system-agnostic). The LABEL is the
    // customer-facing display value in their chosen system.
    const activeSystem = SIZE_SYSTEMS[state.sizeSystem] || SIZE_SYSTEMS.us_ca;
    const sizeOptions = [
      { label: activeSystem.edgeSmallLabel, value: '__edge_small', edge: true },
      { label: activeSystem.edgeLargeLabel, value: '__edge_large', edge: true }
    ];
    activeSystem.sizes.forEach(function (displaySize) {
      const usSize = displaySize - activeSystem.offset;
      sizeOptions.push({
        label: String(displaySize),
        value: String(usSize),
        // Stash the display + system on the option so the click handler
        // can record the reported value for cart attributes.
        displaySize: String(displaySize),
        sizeSystem: activeSystem.id,
      });
    });

    // Question titles read from theme-editor settings (with {name} token
    // substitution applied at render time, so a customer who entered their
    // name on intro sees personalised titles).
    const titleFor = function (id) {
      return personaliseCopy(QUESTION_TITLES[id], state.firstName);
    };

    return [
      {
        id: 'size', eyebrow: 'Sizing',
        title: titleFor('size'), helper: 'Select one',
        type: 'single', autoAdvance: true, layout: 'sizes',
        options: sizeOptions,
      },
      {
        id: 'silhouette', eyebrow: 'Silhouette',
        title: titleFor('silhouette'), helper: 'Select one',
        type: 'single', autoAdvance: true, layout: 'silhouette-3',
        options: [
          { label: 'One-piece',       value: 'one-piece',       diagramHTML: silhouetteIconHTML('onePiece') },
          { label: 'Bikini set',      value: 'bikini',          diagramHTML: silhouetteIconHTML('bikini') },
          { label: 'Dress or romper', value: 'dress-or-romper', diagramHTML: silhouetteIconHTML('dressOrRomper') },
        ],
      },
      {
        id: 'style', eyebrow: 'Top style',
        title: titleFor('style'), helper: 'Pick up to 3',
        type: 'multi', max: 3, layout: '4col', skippable: true,
        options: styleOpts,
      },
      {
        id: 'coverage', eyebrow: 'Bottom coverage',
        title: titleFor('coverage'), helper: 'Pick up to 3',
        type: 'multi', max: 3, layout: '3col', skippable: true, skipForOnePiece: true,
        options: coverageOpts,
      },
      {
        id: 'colour', eyebrow: 'Colour',
        title: titleFor('colour'), helper: 'Pick up to 3 · optional',
        type: 'multi', max: 3, layout: '4col', optional: true, skippable: true,
        options: colourOpts,
      },
    ];
  }

  function getActiveQuestions() {
    return buildQuestions().filter(function (q) {
      // Dress / romper path: skip Q3 (style), Q4 (coverage), Q5 (colour) —
      // we only need size + silhouette before redirecting to the filtered
      // bodywear collection.
      if (state.answers.silhouette === 'dress-or-romper' &&
          (q.id === 'style' || q.id === 'coverage' || q.id === 'colour')) return false;
      if (q.skipForOnePiece && state.answers.silhouette === 'one-piece') return false;
      if (q.id === 'colour' && !CONFIG.enableColourQuestion) return false;
      return true;
    });
  }

  // ============================================================
  // RENDERING
  // ============================================================
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Theme editor textareas preserve line breaks as \n in the JSON config.
  // Convert them to <br> so they render visually. Applies to any multi-line
  // setting (intro body, edge-case bodies).
  function nl2br(s) {
    return String(s == null ? '' : s).replace(/\n/g, '<br>');
  }

  function render() {
    if (!state.bodyEl) return;

    // The email step mounts its Skip button in the modal footer (chrome,
    // outside bodyEl) — remove any leftover on every render so Back
    // navigation or reopen-after-close never leaves a stray Skip behind.
    const straySkip = document.getElementById('quiz-email-step-skip');
    if (straySkip) straySkip.remove();

    // Refresh the sub-header strip first — it lives in modal chrome
    // (outside state.bodyEl) so it's not affected by innerHTML rewrites.
    renderSystemStrip();

    if (state.fetchError && !state.fetched) {
      renderFetchError();
      return;
    }

    if (state.completed) {
      renderCompletion();
      return;
    }

    if (state.onIntro) {
      renderIntro();
      return;
    }

    if (state.onNameStep) {
      renderNameStep();
      return;
    }

    // For questions Q3+ we need catalogue data
    if (state.currentIndex >= 2 && !state.fetched) {
      renderLoading();
      return;
    }

    const qs = getActiveQuestions();
    const q = qs[state.currentIndex];
    if (!q) {
      renderCompletion();
      return;
    }

    const progress = ((state.currentIndex + 1) / qs.length) * 100;
    state.progressEl.style.width = progress + '%';
    state.progressEl.parentElement.style.visibility = 'visible';
    state.stepEl.textContent = 'Step ' + (state.currentIndex + 1) + ' of ' + qs.length;
    state.stepEl.style.visibility = 'visible';

    state.backEl.hidden = false;
    state.continueEl.style.visibility = 'visible';

    const isReRender = state.lastRenderedQuestionId === q.id;
    if (!isReRender) {
      track('quiz_question_viewed', { question_id: q.id, question_number: state.currentIndex + 1 });
    }

    state.bodyEl.innerHTML = renderQuestionHTML(q, isReRender);
    state.lastRenderedQuestionId = q.id;

    state.bodyEl.querySelectorAll('.quiz-option').forEach(function (el) {
      el.addEventListener('click', function () { handleOptionClick(q, el); });
    });
    // Note: size-system pills live in the modal sub-header (chrome), wired
    // up by renderSystemStrip() — not here.
    const skip = state.bodyEl.querySelector('.quiz-skip');
    if (skip) skip.addEventListener('click', function () { handleSkip(q); });

    updateContinueState(q);
  }

  // ============================================================
  // SIZE SYSTEM SUB-HEADER STRIP — modal chrome (NOT inside the
  // question body, so it survives innerHTML rewrites). Visible only
  // when:
  //   1. the customer is on Q1 (the size question),
  //   2. they're not on intro/completion/loading/error screens, AND
  //   3. more than one size system is enabled in theme editor.
  // Otherwise the strip is hidden so it doesn't add chrome when
  // there's nothing to switch.
  // ============================================================
  function renderSystemStrip() {
    if (!state.systemStripEl || !state.systemPillsEl) return;
    const enabledSystems = getEnabledSizeSystems();
    const onQ1 = !state.onIntro
      && !state.completed
      && !state.fetchError
      && state.currentIndex === 0;
    const shouldShow = onQ1 && enabledSystems.length > 1;
    if (!shouldShow) {
      state.systemStripEl.hidden = true;
      state.systemStripEl.classList.remove('is-visible');
      return;
    }
    state.systemPillsEl.innerHTML = enabledSystems.map(function (sysId) {
      const sys = SIZE_SYSTEMS[sysId];
      const isActive = state.sizeSystem === sysId;
      return '<button type="button" class="quiz-size-system-pill' +
        (isActive ? ' is-active' : '') + '" ' +
        'data-size-system="' + sysId + '" ' +
        'role="radio" aria-checked="' + (isActive ? 'true' : 'false') + '">' +
        sys.pillLabel +
        '</button>';
    }).join('');
    // Wire click handlers (replace any prior bindings cleanly by replacing
    // innerHTML above — no leaks because the old nodes are GC'd).
    state.systemPillsEl.querySelectorAll('.quiz-size-system-pill').forEach(function (el) {
      el.addEventListener('click', function () { handleSizeSystemPillClick(el); });
    });
    state.systemStripEl.hidden = false;
    state.systemStripEl.classList.add('is-visible');
  }

  // Swap the active size system, re-render Q1 with the new system's
  // display values, and (if there's an existing US-normalized size
  // selection) preserve it visually by keeping state.answers.size and
  // updating state.answers.sizeReported to reflect the new system's
  // display + prefix.
  function handleSizeSystemPillClick(el) {
    const newSystemId = el.dataset.sizeSystem;
    if (!newSystemId || !SIZE_SYSTEMS[newSystemId]) return;
    if (state.sizeSystem === newSystemId) return;
    state.sizeSystem = newSystemId;
    // If there's already a US-normalized size answer, translate it to
    // the new system's display value (or drop it if out of range).
    if (state.answers.size) {
      const usSize = parseInt(state.answers.size, 10);
      const newDisplay = usSizeToDisplay(usSize, newSystemId);
      if (newDisplay) {
        state.answers.sizeReported = formatReportedSize(newDisplay, newSystemId);
      } else {
        delete state.answers.size;
        delete state.answers.sizeReported;
      }
    }
    // Persist for next session.
    saveQuizState({
      answers: state.answers,
      sizeSystem: state.sizeSystem,
      timestamp: Date.now(),
    });
    // Re-render Q1 with the new system. Force isReRender path by
    // clearing lastRenderedQuestionId mismatch — but we DO want the
    // anim suppressed, so re-render manually.
    state.lastRenderedQuestionId = null;
    render();
  }

  function renderIntro() {
    state.lastRenderedQuestionId = null;
    track('quiz_intro_viewed');

    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-intro">' +
          '<div class="quiz-intro__eyebrow">' + INTRO.eyebrow + '</div>' +
          '<h2 class="quiz-intro__headline">' + INTRO.headline + '</h2>' +
          '<p class="quiz-intro__body">' + nl2br(INTRO.body) + '</p>' +
          (INTRO.prompt ? '<p class="quiz-intro__prompt">' + INTRO.prompt + '</p>' : '') +
          '<button type="button" class="quiz-intro__cta" id="quiz-intro-start">' +
            INTRO.buttonLabel +
            ' <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polyline points="5,2 11,7 5,12"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';

    state.stepEl.style.visibility = 'hidden';
    state.progressEl.parentElement.style.visibility = 'hidden';
    state.backEl.hidden = true;
    state.continueEl.style.visibility = 'hidden';

    const startBtn = document.getElementById('quiz-intro-start');
    if (startBtn) startBtn.addEventListener('click', function () {
      track('quiz_intro_started', { has_first_name: !!state.firstName });
      state.onIntro = false;
      // Route to the dedicated name step if enabled, otherwise skip
      // straight to Q1.
      if (NAME_CAPTURE.enabled) {
        state.onNameStep = true;
      } else {
        state.onNameStep = false;
        state.currentIndex = 0;
      }
      render();
    });
  }

  // ============================================================
  // NAME STEP — dedicated screen between intro and Q1.
  // Only shown when NAME_CAPTURE.enabled. Captures first name (optional),
  // shows a brief warm confirmation message before advancing to Q1.
  // ============================================================
  function renderNameStep() {
    state.lastRenderedQuestionId = null;
    track('quiz_name_step_viewed');

    const safePlaceholder = escapeAttr(NAME_CAPTURE.placeholder || '');
    const safeValue       = escapeAttr(state.firstName || '');

    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-name-step" data-quiz-name-step>' +
          '<h2 class="quiz-intro__headline quiz-name-step__heading" data-quiz-name-step-heading>' + NAME_CAPTURE.heading + '</h2>' +
          '<input type="text" class="quiz-intro__name quiz-name-step__input" id="quiz-name-step-input" ' +
            'autocomplete="given-name" maxlength="60" ' +
            'placeholder="' + safePlaceholder + '" ' +
            'value="' + safeValue + '" />' +
          '<button type="button" class="quiz-intro__cta quiz-name-step__next" id="quiz-name-step-next">' +
            '<span class="quiz-name-step__next-label">' + NAME_CAPTURE.buttonLabel + '</span>' +
            ' <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polyline points="5,2 11,7 5,12"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';

    state.stepEl.style.visibility = 'hidden';
    state.progressEl.parentElement.style.visibility = 'hidden';
    // Back button visible so customer can return to intro if they want.
    state.backEl.hidden = false;
    state.continueEl.style.visibility = 'hidden';

    const input    = document.getElementById('quiz-name-step-input');
    const nextBtn  = document.getElementById('quiz-name-step-next');
    const heading  = state.bodyEl.querySelector('[data-quiz-name-step-heading]');
    const labelEl  = nextBtn.querySelector('.quiz-name-step__next-label');

    let advanceLock = false;

    function advanceToQ1() {
      state.onNameStep = false;
      state.currentIndex = 0;
      render();
    }

    function commit() {
      if (advanceLock) return;
      advanceLock = true;
      const captured = capitaliseName(input.value);
      state.firstName = captured;
      // Persist now so a reload between name step and Q1 remembers it.
      saveQuizState({
        firstName: state.firstName,
        sizeSystem: state.sizeSystem,
        timestamp: Date.now(),
      });
      track('quiz_name_captured', { has_first_name: !!captured });

      // If no name entered: silent advance, no confirmation message.
      if (!captured) {
        advanceToQ1();
        return;
      }

      // Personalised confirmation message + spinner on button. Heading
      // swaps to "We're glad you're here, Julian." for the configured
      // duration before advancing to Q1.
      heading.innerHTML = personaliseCopy(NAME_CAPTURE.confirmationMsg, captured);
      // Hide the input — they've committed their answer, no more editing.
      // The CSS class smoothly collapses height + opacity so the layout
      // doesn't jump.
      input.disabled = true;
      input.classList.add('is-hidden');
      // Replace button label with spinner.
      nextBtn.classList.add('is-loading');
      nextBtn.disabled = true;
      labelEl.innerHTML = '<span class="quiz-name-step__spinner" aria-hidden="true"></span>';

      const delay = parseInt(NAME_CAPTURE.confirmationDurationMs, 10) || 2000;
      setTimeout(advanceToQ1, delay);
    }

    nextBtn.addEventListener('click', commit);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      }
    });

    // Autofocus the input on desktop only (avoid mobile keyboard popup
    // which jank the modal layout). Mobile users can tap to focus.
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    }
  }

  function renderLoading() {
    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-loading">' +
          '<div class="quiz-loading__spinner" aria-hidden="true"></div>' +
          '<p class="quiz-loading__text">Loading your matches…</p>' +
        '</div>' +
      '</div>';
    state.continueEl.style.visibility = 'hidden';
  }

  function renderFetchError() {
    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-edge">' +
          '<h2 class="quiz-edge__title">Something’s not loading</h2>' +
          '<p class="quiz-edge__body">We’re having trouble loading your match data right now.</p>' +
          '<div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap;">' +
            '<button type="button" class="quiz-complete__see" id="quiz-retry">Try again</button>' +
            '<button type="button" class="quiz-edge__link" id="quiz-browse">Browse all suits</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    state.continueEl.style.visibility = 'hidden';
    state.backEl.hidden = true;

    const retry = document.getElementById('quiz-retry');
    if (retry) retry.addEventListener('click', function () {
      state.fetchError = null;
      render();
      loadCatalogueWithRetry().then(render, render);
    });
    const browse = document.getElementById('quiz-browse');
    if (browse) browse.addEventListener('click', function () {
      window.location.href = '/collections/' + CONFIG.fallbackCollectionHandle;
    });
  }

  function renderQuestionHTML(q, isReRender) {
    let options = q.options.slice();
    options = options.map(function (opt) {
      if (opt.edge) return Object.assign({}, opt, { count: null });
      // Colour options use the precomputed per-group count from refreshColourCounts.
      // Zero-count colours render as visually disabled tiles with "None matching".
      if (q.id === 'colour' && typeof opt.precomputedCount === 'number') {
        return Object.assign({}, opt, {
          count: opt.precomputedCount,
          disabled: opt.precomputedCount === 0,
        });
      }
      const count = countForOption(q.id, opt.value);
      return Object.assign({}, opt, { count: count });
    });
    // Hide zero-count options for style + coverage (catalogue actually
    // doesn't carry them). For colour we always show every group — zero-match
    // ones render disabled with a "None matching" label so customers still
    // see the full Youswim colour range.
    options = options.filter(function (o) {
      if (o.edge) return true;
      if (q.id === 'size' || q.id === 'silhouette') return true;
      if (q.id === 'colour') return true;
      return o.count > 0;
    });

    const selected = state.answers[q.id] || (q.type === 'multi' ? [] : null);

    function renderOption(opt) {
      const isSelected = q.type === 'multi'
        ? selected.indexOf(opt.value) !== -1
        : selected === opt.value;
      const isDisabled = opt.disabled === true;
      // Count badge is a scarcity nudge for narrowing-down questions
      // (Q3 style, Q4 coverage). On Q1 (size) and Q2 (silhouette) it
      // misleads — those are foundational choices where one option
      // happening to have ≤4 matches (e.g. dress-or-romper) makes it
      // look "limited" vs the larger categories.
      const showCount = !opt.edge
        && q.id !== 'colour'
        && q.id !== 'size'
        && q.id !== 'silhouette'
        && opt.count !== null && opt.count > 0 && opt.count <= 4;
      const diagramHTML = opt.diagramHTML
        ? '<div class="quiz-option__diagram">' + opt.diagramHTML + '</div>'
        : '';
      const cls = 'quiz-option' +
        (isSelected ? ' is-selected' : '') +
        (isDisabled ? ' is-disabled' : '');
      const extraAttrs =
        (opt.displaySize ? ' data-display-size="' + escapeAttr(opt.displaySize) + '"' : '') +
        (opt.sizeSystem  ? ' data-size-system="'  + escapeAttr(opt.sizeSystem)  + '"' : '');
      return '<button type="button" class="' + cls + '" ' +
                'data-value="' + escapeAttr(opt.value) + '"' + extraAttrs + ' ' +
                'aria-pressed="' + (isSelected ? 'true' : 'false') + '" ' +
                (isDisabled ? 'disabled aria-disabled="true"' : '') + '>' +
              '<span class="quiz-option__check" aria-hidden="true">✓</span>' +
              (showCount ? '<span class="quiz-option__count">' + opt.count + (opt.count === 1 ? ' match' : ' matches') + '</span>' : '') +
              diagramHTML +
              '<span class="quiz-option__label">' + opt.label + '</span>' +
              (isDisabled ? '<span class="quiz-option__sublabel">None matching</span>' : '') +
            '</button>';
    }

    let optionsHTML;
    if (q.layout === 'sizes') {
      // Size-system switcher pills are rendered into the modal sub-header
      // strip (modal chrome, NOT inside the body), so they're not part of
      // optionsHTML here. See renderSystemStrip().
      const edgeOpts = options.filter(function (o) { return o.edge; });
      const numberOpts = options.filter(function (o) { return !o.edge; });
      optionsHTML =
        '<div class="quiz-edge-row">' + edgeOpts.map(renderOption).join('') + '</div>' +
        '<div class="quiz-size-grid">' + numberOpts.map(renderOption).join('') + '</div>';
    } else {
      optionsHTML = options.map(renderOption).join('');
    }

    let multiInfo = '';
    if (q.type === 'multi') {
      multiInfo = '<div class="quiz-multi-info">' + selected.length + ' of ' + q.max + ' selected</div>';
    }

    // "Currently matching" total considers answers up to and INCLUDING
    // the current question — so it reflects the customer's progressively
    // narrowing selections without being prematurely narrowed by
    // answers to questions further along (set on a prior walk-through).
    const totalMatches = countMatchingProducts(answersUpTo(q.id, true));
    const resultLine = state.currentIndex > 0
      ? '<div class="quiz-result-count" role="status" aria-live="polite">Currently matching <strong>' + totalMatches + '</strong> suits</div>'
      : '';

    const skipHTML = q.skippable
      ? '<button type="button" class="quiz-skip">Skip — show me all options</button>'
      : '';

    const animClass = isReRender ? ' is-no-anim' : '';

    return (
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-question' + animClass + '">' +
          '<div class="quiz-question__eyebrow">' + q.eyebrow + '</div>' +
          '<h2 class="quiz-question__title">' + q.title + '</h2>' +
          (q.helper ? '<p class="quiz-question__helper">' + q.helper + '</p>' : '') +
          multiInfo +
          '<div class="quiz-options quiz-options--' + q.layout + '">' + optionsHTML + '</div>' +
          skipHTML +
          resultLine +
        '</div>' +
      '</div>'
    );
  }

  // ============================================================
  // INTERACTION
  // ============================================================
  function handleOptionClick(q, el) {
    const value = el.dataset.value;

    if (value === '__edge_small' || value === '__edge_large') {
      renderEdgeCase(value);
      return;
    }

    if (q.type === 'multi') {
      const current = state.answers[q.id] || [];
      if (current.indexOf(value) !== -1) {
        state.answers[q.id] = current.filter(function (v) { return v !== value; });
      } else {
        if (current.length >= q.max) return;
        state.answers[q.id] = current.concat([value]);
      }
      track('quiz_answer_recorded', {
        question_id: q.id,
        answer_values: (state.answers[q.id] || []).join(','),
        result_count_at_time_of_answer: countMatchingProducts(state.answers),
      });
      render();
    } else {
      state.answers[q.id] = value;
      // For Q1 (size), also record the customer-facing reported value
      // (e.g. "UK12") so we can capture it on the order. The option's
      // displaySize + sizeSystem come from the dynamically generated
      // sizeOptions array (see getActiveQuestions / sizeOptions).
      if (q.id === 'size') {
        const displaySize = el.dataset.displaySize || value;
        const systemId    = el.dataset.sizeSystem  || state.sizeSystem || 'us_ca';
        state.answers.sizeReported = formatReportedSize(displaySize, systemId);
      }
      track('quiz_answer_recorded', {
        question_id: q.id,
        answer_values: String(value),
        result_count_at_time_of_answer: countMatchingProducts(state.answers),
      });
      render();
      if (q.autoAdvance) setTimeout(advance, 280);
    }
  }

  function handleSkip(q) {
    if (q.type === 'multi') state.answers[q.id] = [];
    else state.answers[q.id] = null;
    track('quiz_question_skipped', { question_id: q.id });
    advance();
  }

  function updateContinueState(q) {
    const cont = state.continueEl;
    const answer = state.answers[q.id];
    if (q.type === 'single' && q.autoAdvance) { cont.style.visibility = 'hidden'; return; }
    cont.style.visibility = 'visible';
    let valid = false;
    if (q.type === 'multi') valid = (answer && answer.length > 0) || q.optional;
    else valid = !!answer;
    cont.disabled = !valid;
  }

  function advance() {
    const qs = getActiveQuestions();
    if (state.currentIndex < qs.length - 1) {
      state.currentIndex++;
      // If we just advanced TO the colour question, recompute per-group
      // availability from state.variants + mapping.
      const nextQ = qs[state.currentIndex];
      if (nextQ && nextQ.id === 'colour') refreshColourCounts();
      render();
    } else {
      // After the last question, decide whether to show the email
      // interstitial or skip straight to the summary. The interstitial
      // only shows when:
      //   1. Master toggle enabled
      //   2. Not already shown in THIS attempt (per-state flag)
      //   3. The customer has never previously submitted an email
      const shouldShowEmailStep =
        EMAIL_CAPTURE.enabled
        && !state.emailInterstitialShown
        && !hasEmailEverBeenCaptured();
      if (shouldShowEmailStep) {
        renderEmailInterstitial();
      } else {
        renderCompletion();
      }
    }
  }

  function goBack() {
    const qs = getActiveQuestions();
    const currentQ = qs[state.currentIndex];
    track('quiz_back_clicked', { question_id: state.completed ? 'summary' : (currentQ ? currentQ.id : null) });
    // From summary: drop back into the last active question for editing.
    if (state.completed) {
      state.completed = false;
      state.currentIndex = Math.max(0, qs.length - 1);
      render();
      return;
    }
    if (state.currentIndex > 0) { state.currentIndex--; render(); }
    else if (state.onNameStep) {
      // Back from name step → return to intro
      state.onNameStep = false;
      state.onIntro = true;
      render();
    }
    else if (NAME_CAPTURE.enabled) {
      // Back from Q1 → return to name step (skipping past intro)
      state.onNameStep = true;
      render();
    }
    else {
      // Back from Q1 with name capture disabled → return to intro
      state.onIntro = true;
      render();
    }
  }

  // ============================================================
  // EDGE CASE
  // ============================================================
  function renderEdgeCase(which) {
    state.lastRenderedQuestionId = null;
    const isSmall = which === '__edge_small';
    track('quiz_edge_size_reached', { which_edge: isSmall ? 'smaller_than_us_2' : 'larger_than_us_24' });
    const title = isSmall ? EDGE.smallTitle : EDGE.largeTitle;
    const body  = isSmall ? EDGE.smallBody  : EDGE.largeBody;

    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-edge">' +
          '<div class="quiz-edge__icon"><svg viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="28" cy="28" r="20"/><path d="M22 28h12M28 22v12"/></svg></div>' +
          '<h2 class="quiz-edge__title">' + title + '</h2>' +
          '<p class="quiz-edge__body">' + nl2br(body) + '</p>' +
          '<form class="quiz-edge__email" id="quiz-edge-form">' +
            '<input type="email" required placeholder="your@email.com" aria-label="Email address" />' +
            '<button type="submit">Notify me</button>' +
          '</form>' +
          '<button type="button" class="quiz-edge__link" id="quiz-edge-browse">Or browse our current range</button>' +
        '</div>' +
      '</div>';

    state.continueEl.style.visibility = 'hidden';
    state.backEl.hidden = false;

    const form = document.getElementById('quiz-edge-form');
    if (form) {
      // On mobile, the soft keyboard pushes the email input off-screen on
      // focus. Scroll it into the centre after a short delay (Safari needs
      // the wait — the visualViewport doesn't change immediately).
      const emailInput = form.querySelector('input[type=email]');
      if (emailInput) emailInput.addEventListener('focus', function () {
        setTimeout(function () {
          if (typeof emailInput.scrollIntoView === 'function') {
            emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      });
    }
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      const email = form.querySelector('input[type=email]').value.trim();
      if (!email) return;
      const edgeName = isSmall ? 'smaller_than_us_2' : 'larger_than_us_24';
      track('quiz_email_captured', { which_edge: edgeName });
      submitEdgeCaseEmail(email, edgeName).then(function () {
        form.innerHTML = '<p style="font-family:var(--quiz-font-display);font-style:italic;color:var(--quiz-color-success);font-size:15px;margin:0;">Thanks — we’ll be in touch.</p>';
      }, function () {
        form.innerHTML = '<p style="font-family:var(--quiz-font-display);font-style:italic;color:var(--quiz-color-text-muted);font-size:15px;margin:0;">We received your email — thanks.</p>';
      });
    });

    const browse = document.getElementById('quiz-edge-browse');
    if (browse) browse.addEventListener('click', closeQuiz);
  }

  function submitEdgeCaseEmail(email, edgeCase) {
    if (!CONFIG.klaviyoCompanyId || !CONFIG.klaviyoListId) {
      // Not configured — acknowledge but don't submit
      console.warn('[Youswim Quiz] Klaviyo not configured; edge-case email captured locally only.', { email: email, edgeCase: edgeCase });
      return Promise.resolve();
    }
    const url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(CONFIG.klaviyoCompanyId);
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Youswim quiz — size edge case (' + edgeCase + ')',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: { youswim_edge_case: edgeCase },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: CONFIG.klaviyoListId } } },
      },
    };
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('klaviyo-' + res.status);
    });
  }

  // ============================================================
  // EMAIL CAPTURE INTERSTITIAL (between last question and summary)
  //
  // Shown post-Q-last to general completers. Always optional — Continue
  // with empty input and Skip both advance silently to the summary.
  // Klaviyo profile is created only when the customer submits a valid
  // email. If state.firstName was captured on intro, it's attached as
  // the standard Klaviyo first_name attribute (NOT a custom property)
  // so Klaviyo personalisation tokens like {{ first_name }} work.
  // ============================================================
  function submitCompleterEmail(email) {
    if (!CONFIG.klaviyoCompanyId || !CONFIG.klaviyoListId) {
      console.warn('[Youswim Quiz] Klaviyo not configured; completer email captured locally only.', { email: email });
      return Promise.resolve();
    }
    const url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(CONFIG.klaviyoCompanyId);
    // Build the profile attributes payload. first_name is only included
    // when non-empty after trim — never sent as an empty string.
    const profileAttrs = {
      email: email,
      properties: {
        quiz_completer: true,
        quiz_size: state.answers.size || '',
        quiz_size_reported: state.answers.sizeReported || '',
        quiz_silhouette: state.answers.silhouette || '',
        quiz_style: (state.answers.style || []).join(', '),
        quiz_coverage: (state.answers.coverage || []).join(', '),
        quiz_results_url: buildResultsURL(state.answers),
      },
    };
    if (state.firstName) profileAttrs.first_name = state.firstName;

    // Dedicated completer list if configured, else the shared list.
    const listId = CONFIG.klaviyoListIdCompleter || CONFIG.klaviyoListId;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Youswim quiz — completer',
          profile: {
            data: { type: 'profile', attributes: profileAttrs },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    };
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('klaviyo-' + res.status);
    });
  }

  function renderEmailInterstitial() {
    state.lastRenderedQuestionId = null;
    track('quiz_email_step_viewed', {
      has_first_name: !!state.firstName,
    });

    const headline = personaliseCopy(EMAIL_CAPTURE.headline, state.firstName);
    const bodyHTML = EMAIL_CAPTURE.body
      ? '<p class="quiz-email-step__body">' + personaliseCopy(EMAIL_CAPTURE.body, state.firstName) + '</p>'
      : '';

    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-email-step">' +
          '<div class="quiz-intro__eyebrow">' + EMAIL_CAPTURE.eyebrow + '</div>' +
          '<h2 class="quiz-intro__headline">' + headline + '</h2>' +
          bodyHTML +
          '<form class="quiz-email-step__form" id="quiz-email-step-form" novalidate>' +
            '<input type="email" id="quiz-email-step-input" required ' +
              'autocomplete="email" inputmode="email" ' +
              'placeholder="' + escapeAttr(EMAIL_CAPTURE.placeholder || '') + '" ' +
              'aria-label="Email address" />' +
            '<div class="quiz-email-step__validation" id="quiz-email-step-error" hidden>Please enter a valid email address.</div>' +
            '<button type="submit" class="quiz-email-step__continue">' +
              EMAIL_CAPTURE.buttonLabel +
              ' <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polyline points="5,2 11,7 5,12"/></svg>' +
            '</button>' +
          '</form>' +
        '</div>' +
      '</div>';

    // Hide modal chrome that doesn't apply here (no progress bar, no Continue
    // in the footer — the step's own button handles advance).
    state.stepEl.textContent = '';
    state.stepEl.style.visibility = 'hidden';
    state.progressEl.style.width = '100%';
    state.backEl.hidden = false;
    state.continueEl.style.visibility = 'hidden';

    const form    = document.getElementById('quiz-email-step-form');
    const input   = document.getElementById('quiz-email-step-input');
    const errorEl = document.getElementById('quiz-email-step-error');

    // Skip lives in the modal FOOTER — same slot as the Continue button
    // on question steps (bottom-right, opposite Back). Appended here and
    // removed on every exit path (render() cleans it up too, covering
    // Back-navigation and reopen-after-close).
    const footerEl = state.modalEl.querySelector('.quiz-modal__footer');
    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'quiz-email-step__skip';
    skipBtn.id = 'quiz-email-step-skip';
    skipBtn.textContent = EMAIL_CAPTURE.skipLabel;
    if (footerEl) footerEl.appendChild(skipBtn);

    // Helper: advance to summary, marking the per-attempt suppression so
    // back-button navigation from summary doesn't re-trigger.
    function advanceToSummary() {
      state.emailInterstitialShown = true;
      skipBtn.remove();  // footer-mounted — body innerHTML wipe won't clear it
      renderCompletion();
    }

    // Skip handler — never marks permanent capture, so skippers get
    // another chance on the next restart.
    skipBtn.addEventListener('click', function () {
      track('quiz_email_skipped', { reason: 'skip_clicked' });
      advanceToSummary();
    });

    // Submit handler — empty input = silent skip; invalid = inline error
    // (no advance); valid = submit + advance.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const raw = (input.value || '').trim();

      // Empty Continue is treated as silent skip — not an error.
      if (!raw) {
        track('quiz_email_skipped', { reason: 'continue_empty' });
        advanceToSummary();
        return;
      }
      // Basic email validation: presence of `@` with non-empty local
      // and domain parts. We deliberately keep this lenient; Klaviyo
      // will do its own validation on the server.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
        errorEl.hidden = false;
        input.focus();
        return;
      }
      errorEl.hidden = true;

      // Fire-and-forget submit + advance. Show success momentarily before
      // advancing so the customer sees their input was acknowledged.
      const domain = raw.split('@')[1] || '';
      track('quiz_email_captured_completer', {
        has_first_name: !!state.firstName,
        email_domain: domain,
      });
      markEmailCaptured();

      // Replace form with brief success state.
      const formContainer = form.parentNode;
      const successEl = document.createElement('div');
      successEl.className = 'quiz-email-step__success';
      successEl.textContent = EMAIL_CAPTURE.successMsg || 'Thanks — you’re in.';
      form.replaceWith(successEl);
      if (skipBtn) skipBtn.remove();

      submitCompleterEmail(raw).catch(function (err) {
        // Network failure is silent — we never block the customer's
        // path to their matches on a third-party POST.
        console.warn('[Youswim Quiz] Completer Klaviyo POST failed', err);
      });

      // Brief pause so the success state is visible before navigation.
      setTimeout(advanceToSummary, 700);
    });

    // Autofocus the input on desktop only (avoid mobile keyboard popup
    // which would jank the modal layout).
    if (input && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    }
  }

  // ============================================================
  // FILTER RELAXATION (spec §4.6, adapted)
  // Only relax when the strict combo would return ZERO products. Spec said 3,
  // but that was assuming variant-level counts; with product-level counts a
  // threshold of 3 strips the customer's selections too aggressively. Showing
  // a single matching product is preferable to ignoring their choices.
  // ============================================================
  const RELAX_THRESHOLD = 1;

  function relaxFilters(answers) {
    let candidate = Object.assign({}, answers);
    const relaxed = [];
    if (countMatchingProducts(candidate) >= RELAX_THRESHOLD) return { answers: candidate, relaxed: relaxed };

    // 1. Colour
    if (candidate.colour && candidate.colour.length) {
      candidate = Object.assign({}, candidate, { colour: [] });
      relaxed.push('colour');
      if (countMatchingProducts(candidate) >= RELAX_THRESHOLD) return { answers: candidate, relaxed: relaxed };
    }
    // 2. Style
    if (candidate.style && candidate.style.length) {
      candidate = Object.assign({}, candidate, { style: [] });
      relaxed.push('style');
      if (countMatchingProducts(candidate) >= RELAX_THRESHOLD) return { answers: candidate, relaxed: relaxed };
    }
    // 3. Coverage (bikini path only — never for one-piece)
    if (candidate.silhouette === 'bikini' && candidate.coverage && candidate.coverage.length) {
      candidate = Object.assign({}, candidate, { coverage: [] });
      relaxed.push('coverage');
    }
    return { answers: candidate, relaxed: relaxed };
  }

  // ============================================================
  // COMPLETION
  // ============================================================
  function renderCompletion() {
    state.lastRenderedQuestionId = null;
    // Apply relaxation
    const result = relaxFilters(state.answers);
    state.relaxed = result.relaxed;
    const finalAnswers = result.answers;
    const matchCount = countMatchingProducts(finalAnswers);
    const narrative = buildNarrative(finalAnswers, matchCount, state.relaxed);
    const url = buildResultsURL(finalAnswers);

    const wasJustCompleted = !state.completed;
    state.completed = true;

    state.bodyEl.innerHTML =
      '<div class="quiz-modal__body-inner">' +
        '<div class="quiz-complete">' +
          '<div class="quiz-complete__sparkle" aria-hidden="true"><svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor"><path d="M9 2L10 8L16 9L10 10L9 16L8 10L2 9L8 8Z"/><path d="M18 2L18.7 4.3L21 5L18.7 5.7L18 8L17.3 5.7L15 5L17.3 4.3Z"/><path d="M19 15L19.5 16.5L21 17L19.5 17.5L19 19L18.5 17.5L17 17L18.5 16.5Z"/></svg></div>' +
          '<h2 class="quiz-complete__title">' + personaliseCopy(SUMMARY.headline, state.firstName) + '</h2>' +
          '<div class="quiz-complete__narrative">' + narrative + '</div>' +
          '<div class="quiz-complete__actions">' +
            '<button type="button" class="quiz-complete__see" id="quiz-see-matches">' +
              'See your matches' +
              ' <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polyline points="5,2 11,7 5,12"/></svg>' +
            '</button>' +
            '<button type="button" class="quiz-complete__retake" id="quiz-retake">Restart quiz</button>' +
          '</div>' +
        '</div>' +
      '</div>';

    state.continueEl.style.visibility = 'hidden';
    // Back button stays available so the customer can step back into a question
    // and edit their answers without nuking everything.
    state.backEl.hidden = false;
    state.progressEl.style.width = '100%';
    state.stepEl.textContent = 'Complete';

    // Persist completed state for cross-session resume.
    // Save the set of matching product handles so PDPs can detect whether
    // the customer landed on a quiz-recommended product (drives the
    // "Recommended for you" badge).
    const matchingHandles = [];
    const seenHandles = {};
    getMatchingVariants(finalAnswers).forEach(function (v) {
      if (!seenHandles[v.productHandle]) {
        seenHandles[v.productHandle] = true;
        matchingHandles.push(v.productHandle);
      }
    });
    saveQuizState({
      url: url,
      answers: state.answers,
      relaxed: state.relaxed,
      matchCount: matchCount,
      matchingProducts: matchingHandles,
      sizeSystem: state.sizeSystem,
      firstName: state.firstName,
      emailInterstitialShown: state.emailInterstitialShown,
      timestamp: Date.now(),
    });

    if (wasJustCompleted) {
      track('quiz_completed', Object.assign({
        total_matches: matchCount,
        relaxed_filters: (state.relaxed || []).join(',') || null,
        final_url: url,
        source: state.openSource,
        // Which catalogue this run counted against — 'sale' during sale-mode
        // launches, else the default catalogue handle. For segmentation.
        catalogue_context: getEffectiveCatalogueHandle(),
        // Each answer as its own param — GA4 custom dimensions can break these out
        answer_size: state.answers.size || null,
        answer_silhouette: state.answers.silhouette || null,
        answer_style: (state.answers.style || []).join(',') || null,
        answer_coverage: (state.answers.coverage || []).join(',') || null,
        answer_colour: (state.answers.colour || []).join(',') || null,
      }, state.launchContext || {}));
    }

    // Attach quiz answers to cart so they appear on the resulting order
    // (admin → Order details → Additional details). Underscore prefix keeps
    // them hidden from the customer's checkout summary. Fire-and-forget;
    // any failure here must never block the quiz UX.
    attachQuizToCart(state.answers, matchCount, url, state.launchContext);

    const seeBtn = document.getElementById('quiz-see-matches');
    if (seeBtn) seeBtn.addEventListener('click', function () {
      if (seeBtn.classList.contains('is-loading')) return; // dedupe double-clicks
      seeBtn.classList.add('is-loading');
      seeBtn.setAttribute('aria-busy', 'true');
      track('quiz_see_matches_clicked');

      // Replace button content with a left-aligned spinner + rotating
      // charming messages. Each message shows for 1s; after the last
      // one finishes its second, navigation begins. Messages come from
      // theme editor (Find Your Fit quiz section → Loading messages).
      // Empty messages are dropped; if none remain a fallback is used.
      let messages = (SUMMARY.loadingMessages || []).map(function (m) { return (m || '').trim(); }).filter(Boolean);
      if (!messages.length) messages = ['Loading your matches…'];
      seeBtn.innerHTML =
        '<span class="quiz-complete__see-spinner" aria-hidden="true"></span>' +
        '<span class="quiz-complete__see-message">' + messages[0] + '</span>';

      const msgEl = seeBtn.querySelector('.quiz-complete__see-message');
      const duration = parseInt(SUMMARY.loadingMessageDurationMs, 10) || 1000;
      let i = 0;
      const interval = setInterval(function () {
        i++;
        if (i < messages.length) {
          msgEl.textContent = messages[i];
        } else {
          clearInterval(interval);
          navigateToResults(url);
        }
      }, duration);
    });
    const retake = document.getElementById('quiz-retake');
    if (retake) retake.addEventListener('click', function () {
      track('quiz_retake_clicked');
      // Preserve firstName across restart — they've already introduced
      // themselves once, no need to ask again. Save just the name so
      // the next session/reload still has it.
      const preservedName = state.firstName;
      clearQuizState();
      if (preservedName) {
        saveQuizState({
          firstName: preservedName,
          sizeSystem: state.sizeSystem,
          timestamp: Date.now(),
        });
      }
      state.completed = false;
      state.onIntro = false;     // skip intro on retake — straight to Q1
      state.onNameStep = false;  // skip name step too — they're already named
      state.currentIndex = 0;
      state.answers = {};
      state.relaxed = [];
      // firstName intentionally NOT cleared (preserved from prior attempt).
      // Per-attempt email-step suppression resets (they get a fresh chance
      // to enter email). Permanent capture flag is unaffected — if they
      // already submitted on a prior attempt, the step still won't show.
      state.emailInterstitialShown = false;
      render();
    });
  }

  // Cup-size capacity per Stretch range. Used in the summary's sentence 1.
  const STRETCH_TO_CUP = {
    'Stretch I':  'G cup',
    'Stretch II': 'J cup',
  };

  function buildNarrative(answers, matchCount, relaxed) {
    const stretch = dressSizeToStretch(answers.size);
    // Show the size in the customer's chosen region (e.g. "UK/AUS 12"),
    // not the US-normalized internal value. answers.size is always the
    // US-normalized number; we convert back to the active system's
    // display value for prose. Falls back to "US X" for legacy saved
    // state that pre-dates the multi-system feature.
    const systemId = state.sizeSystem || 'us_ca';
    const sys = SIZE_SYSTEMS[systemId] || SIZE_SYSTEMS.us_ca;
    const usSizeNum = parseInt(answers.size, 10);
    const displaySize = !isNaN(usSizeNum) ? usSizeToDisplay(usSizeNum, systemId) : null;
    const dressSize = displaySize
      ? sys.pillLabel + ' ' + displaySize
      : '';
    const cup = stretch ? STRETCH_TO_CUP[stretch] : null;

    const sentence1 = stretch
      ? 'Our <strong>' + stretch + '</strong> size will easily adapt to your <strong>' + dressSize + '</strong> dress size' +
        (cup ? ', and up to a ' + cup : '') + '.'
      : '';

    // Sentence 2: depends on path.
    //  - one-piece / bikini: name-checks the customer's picked style(s)
    //    using the internal catalogue value (e.g. "Eva", "Poise") rather
    //    than the descriptive quiz label ("Adjustable straps"). This is
    //    deliberate: naming the actual style teaches the customer the
    //    vocabulary they'll see on product cards and across the site.
    //  - dress-or-romper: lists the matching product titles directly
    //    (e.g. "Io Romper and Sol Dress") since this path has no style
    //    question.
    let sentence2 = '';
    if (answers.style && answers.style.length) {
      // Name every style the selection actually pulls in, not just the one
      // clicked — a merged option like "Adjustable straps" (Poise) also
      // matches Juno products, and the customer should be taught both names
      // since both appear in their results. Same expansion the filter URL
      // and match logic use, so prose and results always agree.
      // DISPLAY ONLY: expandStyleValues returns a new array (reduce+concat),
      // so answers.style is never mutated — results logic is untouched.
      const seen = {};
      const names = expandStyleValues(answers.style).filter(function (n) {
        if (seen[n]) return false;
        seen[n] = true;
        return true;
      });
      const boldNames = names.map(function (n) { return '<strong>' + n + '</strong>'; });
      const wordStyle = names.length === 1 ? 'style' : 'styles';
      const verb      = names.length === 1 ? 'is'    : 'are';
      sentence2 = 'We think our ' + joinWithAnd(boldNames) + ' ' + wordStyle + ' ' + verb + ' exactly what you’re looking for.';
    } else if (answers.silhouette === 'dress-or-romper') {
      const matchingVariants = getMatchingVariants(answers);
      const titles = [];
      const seen = {};
      matchingVariants.forEach(function (v) {
        if (v.productTitle && !seen[v.productHandle]) {
          seen[v.productHandle] = true;
          titles.push(v.productTitle);
        }
      });
      if (titles.length) {
        const boldTitles = titles.map(function (t) { return '<strong>' + t + '</strong>'; });
        sentence2 = 'We think you’ll love our ' + joinWithAnd(boldTitles) + '.';
      }
    }

    let relaxedSentence = '';
    if (relaxed && relaxed.length) {
      const human = relaxed.map(function (r) {
        if (r === 'colour') return 'all colours';
        if (r === 'style') return 'all styles';
        if (r === 'coverage') return 'all coverage options';
        return r;
      });
      relaxedSentence = 'We’ve shown ' + joinWithAnd(human) + ' since your preferences didn’t have enough matches.';
    }

    return (sentence1        ? '<span>' + sentence1 + '</span>' : '') +
           (sentence2        ? '<span>' + sentence2 + '</span>' : '') +
           (relaxedSentence  ? '<span>' + relaxedSentence + '</span>' : '');
  }

  function getStyleLabel(internalValue) {
    const o = state.styleOptions.filter(function (x) { return x.internal === internalValue; })[0];
    return o ? o.label : internalValue;
  }
  function getCoverageLabel(internalValue) {
    const o = state.coverageOptions.filter(function (x) { return x.internal === internalValue; })[0];
    return o ? o.label : internalValue;
  }
  function joinWithAnd(items) {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return items[0] + ' and ' + items[1];
    // Oxford comma for 3+
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
  }

  // ============================================================
  // URL BUILDER
  // ============================================================
  function getResultsCollectionHandle() {
    const path = window.location.pathname || '';
    const m = path.match(/\/collections\/([^\/?#]+)/);
    if (m && m[1]) return m[1];
    return CONFIG.fallbackCollectionHandle;
  }

  // Context-aware catalogue selection. When the quiz is launched from a
  // collection page listed in the "Context catalogue collections" theme
  // setting (e.g. the sale collection during a sale event), THAT collection
  // becomes the quiz's catalogue: match counts, Q3/Q4 options and the
  // summary narrative all reflect its stock — and results return there too,
  // via getResultsCollectionHandle's existing launch-collection routing.
  // Launched from anywhere else, the normal catalogue applies. Safe with
  // the per-page fetch cache (state.fetched): the launch context cannot
  // change without a full page load.
  function getEffectiveCatalogueHandle() {
    const m = (window.location.pathname || '').match(/\/collections\/([^\/?#]+)/);
    const launch = m && m[1] ? m[1].toLowerCase() : null;
    if (launch && CONFIG.contextCatalogueHandles.indexOf(launch) !== -1) return launch;
    return CONFIG.catalogueHandle;
  }

  // Navigate to the filtered results URL. Critical wrinkle: if the customer
  // re-opens the quiz from the same collection page they were redirected to
  // last time, the target URL equals the current URL. Setting
  // window.location.href to an identical URL is a no-op in every modern
  // browser — no navigation, no reload — leaving the loading-message cycle
  // frozen on the last message indefinitely (the bug repro from memory).
  // When the target matches, close the modal and scroll to the anchor
  // instead.
  function navigateToResults(targetUrl) {
    let resolved;
    try {
      resolved = new URL(targetUrl, window.location.href);
    } catch (e) {
      window.location.href = targetUrl;
      return;
    }
    const samePathAndQuery =
      resolved.origin   === window.location.origin   &&
      resolved.pathname === window.location.pathname &&
      resolved.search   === window.location.search;
    if (samePathAndQuery) {
      closeQuiz();
      if (resolved.hash) {
        try {
          const el = document.querySelector(resolved.hash);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) { /* ignore invalid selector */ }
      }
      return;
    }
    window.location.href = targetUrl;
  }

  // ============================================================
  // CART ATTRIBUTES — attach quiz answers to the cart so they appear
  // on the resulting order in Shopify admin (Order details → Additional
  // details). Underscore-prefixed keys are hidden from the customer's
  // checkout summary. Shopify's /cart/update.js merges by key, so other
  // apps' attributes are untouched; sending an empty string for a key
  // deletes that key (handy when a customer retakes the quiz via a path
  // that skips earlier questions). Silent on failure — must never block
  // the quiz UX or cart flow.
  // ============================================================
  function attachQuizToCart(answers, matchCount, resultsUrl, launchContext) {
    if (!answers) return;
    const lc = launchContext || {};
    // _quiz_size       → US-normalized number (e.g. "8") for consistent reporting
    //                    across all size systems. Drives the Shopify Flow
    //                    `quiz-size-{value}` order tagging rule.
    // _quiz_size_reported → what the customer actually picked, system-prefixed
    //                       (e.g. "UK12", "EU40", "US8"). For audit / display.
    const payload = {
      attributes: {
        '_quiz_completed_at':   new Date().toISOString(),
        '_quiz_size':           answers.size || '',
        '_quiz_size_reported':  answers.sizeReported || '',
        '_quiz_silhouette':     answers.silhouette || '',
        '_quiz_style':          (answers.style    || []).join(', '),
        '_quiz_coverage':       (answers.coverage || []).join(', '),
        '_quiz_colour':         (answers.colour   || []).join(', '),
        '_quiz_matches_count':  (matchCount == null ? '' : String(matchCount)),
        '_quiz_results_url':    resultsUrl || '',
        '_quiz_source_page':    lc.launch_page_path || '',
        '_quiz_source':         lc.source || ''
      }
    };
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function () { /* silent — quiz UX never blocks on this */ });
    } catch (e) { /* silent */ }
  }

  function buildResultsURL(answers) {
    const handle = getResultsCollectionHandle();
    const params = [];

    const stretch = dressSizeToStretch(answers.size);
    if (stretch) params.push('filter.v.option.size=' + encodeURIComponent(stretch));

    const cutKey   = CONFIG.metafieldKeys.cut;
    const styleKey = CONFIG.metafieldKeys.style;
    const typeKey  = CONFIG.metafieldKeys.type;

    if (answers.silhouette === 'one-piece') {
      params.push('filter.p.m.custom.' + cutKey + '=' + encodeURIComponent(ONE_PIECE_COVERAGE_VALUE));
    } else if (answers.silhouette === 'bikini') {
      // expandCoverageValues: selected Cheeky also emits Brazillian (and the
      // typo'd Bazillian) so those products appear in filtered results.
      // BIKINI_COVERAGE_VALUES already lists Brazillian for the skip case.
      const list = (answers.coverage && answers.coverage.length)
        ? expandCoverageValues(answers.coverage) : BIKINI_COVERAGE_VALUES;
      list.forEach(function (v) { params.push('filter.p.m.custom.' + cutKey + '=' + encodeURIComponent(v)); });
    } else if (answers.silhouette === 'dress-or-romper') {
      params.push('filter.p.m.custom.' + typeKey + '=' + encodeURIComponent(BODYWEAR_TYPE_VALUE));
    }

    if (answers.style && answers.style.length) {
      expandStyleValues(answers.style).forEach(function (v) {
        params.push('filter.p.m.custom.' + styleKey + '=' + encodeURIComponent(v));
      });
    }

    if (answers.colour && answers.colour.length) {
      answers.colour.forEach(function (gid) {
        params.push('filter.v.option.color=' + encodeURIComponent(gid));
      });
    }

    return '/collections/' + handle + (params.length ? '?' + params.join('&') : '') + '#collection-grid';
  }

  // ============================================================
  // MODAL CONTROL
  // ============================================================
  // Snapshot of the page the customer launched the quiz from. Captured on
  // openQuiz and attached to key events for analytics breakdowns ("which
  // collection page drives the most quiz completions?").
  function getLaunchContext() {
    const path = window.location.pathname || '/';
    let pageType = 'other';
    let collectionHandle = null;
    if (path === '/' || path === '') {
      pageType = 'home';
    } else if (path.indexOf('/products/') !== -1) {
      pageType = 'product';
    } else {
      const m = path.match(/\/collections\/([^\/?#]+)/);
      if (m) {
        pageType = 'collection';
        collectionHandle = m[1];
      } else if (path.indexOf('/pages/') !== -1) {
        pageType = 'page';
      } else if (path.indexOf('/blogs/') !== -1) {
        pageType = 'blog';
      }
    }
    return {
      launch_page_path: path,
      launch_page_type: pageType,
      launch_collection_handle: collectionHandle,
    };
  }

  function openQuiz(options) {
    if (!state.modalEl) return;
    options = options || {};
    state.openSource = options.source || 'cta';
    state.launchContext = getLaunchContext();
    state.abandonFired = false;
    state.lastFocusEl = document.activeElement;
    state.modalEl.classList.add('is-open');
    document.body.classList.add('js-quiz-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', focusTrapHandler);

    track('quiz_opened', Object.assign({ source: state.openSource }, state.launchContext));

    // Resume from a prior session if one exists in localStorage.
    const saved = loadQuizState();
    // Restore firstName regardless of whether the customer completed the
    // quiz — even an abandoned attempt at the intro screen saves the name
    // so reopens pre-fill it.
    state.firstName = (saved && saved.firstName) || '';

    if (saved && saved.answers) {
      state.answers = saved.answers;
      state.relaxed = saved.relaxed || [];
      state.completed = true;
      state.onIntro = false;
      state.currentIndex = 0;
      // Per-attempt email-interstitial flag — true if a previous visit
      // showed the step (submit or skip), so navigating back from summary
      // and re-completing doesn't re-trigger it within the same attempt.
      state.emailInterstitialShown = !!saved.emailInterstitialShown;
      // Restore the previously chosen size system, or migrate older saves
      // that pre-date the multi-system feature (treat them as us_ca, and
      // back-fill the reported value so cart attributes are complete).
      state.sizeSystem = saved.sizeSystem || 'us_ca';
      if (saved.answers.size && !saved.answers.sizeReported) {
        const usSize = parseInt(saved.answers.size, 10);
        const display = usSizeToDisplay(usSize, state.sizeSystem) || saved.answers.size;
        state.answers.sizeReported = formatReportedSize(display, state.sizeSystem);
      }
      // Re-attach quiz answers to the cart. Day-2 customers may return
      // with a fresh cart session whose attributes have expired; this
      // ensures anything they add still carries quiz context to the order.
      attachQuizToCart(saved.answers, saved.matchCount, saved.url, state.launchContext);
    } else {
      state.onIntro = options.autostart ? false : true;
      state.completed = false;
      state.currentIndex = 0;
      state.answers = {};
      state.relaxed = [];
      state.emailInterstitialShown = false;
      // Auto-detect the visitor's size system from their country (set
      // by Shopify Markets/IP geolocation). Falls back to the theme-editor
      // "Default size system" if no country match or auto-detect is off.
      state.sizeSystem = detectInitialSizeSystem();
    }
    render();
    const closeBtn = state.modalEl.querySelector('.quiz-modal__close');
    if (closeBtn) closeBtn.focus();

    // Kick off catalogue fetch in the background (during intro display).
    // Re-render the current question whenever the fetch resolves so counts
    // populate retroactively (even if user is still on Q1/Q2).
    if (!state.fetched && !state.fetching) {
      loadCatalogueWithRetry().then(function () {
        if (!state.onIntro) render();
      }, function () {
        if (!state.onIntro) render();
      });
    }
  }

  function closeQuiz() {
    if (!state.modalEl) return;
    // Fire abandon if customer closes mid-flow (not on intro, not completed).
    if (!state.completed && !state.onIntro && !state.abandonFired) {
      const qs = getActiveQuestions();
      const currentQ = qs[state.currentIndex];
      track('quiz_abandoned', Object.assign({
        last_question_id: currentQ ? currentQ.id : null,
        source: state.openSource,
      }, state.launchContext || {}));
      state.abandonFired = true;
    }
    state.modalEl.classList.remove('is-open');
    document.body.classList.remove('js-quiz-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', focusTrapHandler);
    if (state.lastFocusEl && state.lastFocusEl.focus) state.lastFocusEl.focus();
  }

  // Tab-key focus trap: while the modal is open, Tab / Shift+Tab cycle
  // focus only between the modal's own focusable controls (option tiles,
  // back / continue / close, intro start, edge email, summary buttons).
  function focusTrapHandler(e) {
    if (e.key !== 'Tab') return;
    if (!state.modalEl || !state.modalEl.classList.contains('is-open')) return;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = Array.prototype.slice.call(state.modalEl.querySelectorAll(selector))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !state.modalEl.contains(active)) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (active === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }

  // ============================================================
  // DEBUG
  // ============================================================
  window.youswimQuizDebug = {
    state: state,
    config: CONFIG,
    open: function () { openQuiz(); },
    close: function () { closeQuiz(); },
    reloadCatalogue: function () { state.fetched = false; state.fetchError = null; return loadCatalogueWithRetry(); },
    buildURL: function () { return buildResultsURL(state.answers); },
    matches: function () { return getMatchingVariants(state.answers); },
  };

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    state.modalEl    = document.getElementById('quiz-modal');
    if (!state.modalEl) return;
    state.bodyEl     = state.modalEl.querySelector('[data-quiz-body]');
    state.progressEl = state.modalEl.querySelector('[data-quiz-progress]');
    state.stepEl     = state.modalEl.querySelector('[data-quiz-step]');
    state.backEl     = state.modalEl.querySelector('[data-quiz-back]');
    state.continueEl = state.modalEl.querySelector('[data-quiz-continue]');
    state.systemStripEl  = state.modalEl.querySelector('[data-quiz-system-strip]');
    state.systemPillsEl  = state.modalEl.querySelector('[data-quiz-system-pills]');

    const closeBtn = state.modalEl.querySelector('[data-quiz-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeQuiz);
    if (state.backEl) state.backEl.addEventListener('click', goBack);
    if (state.continueEl) state.continueEl.addEventListener('click', advance);

    document.addEventListener('click', function (e) {
      const t = e.target.closest('[data-quiz-open], .js-open-quiz');
      if (t) {
        e.preventDefault();
        // Allow each trigger element to identify itself via data-quiz-source
        // — e.g. <a data-quiz-open data-quiz-source="main-menu">. Defaults to
        // 'cta' if not set. Useful for breaking down completion rates by
        // which CTA was tapped (header link vs hero button vs footer, etc.).
        const sourceAttr = t.getAttribute('data-quiz-source');
        const source = sourceAttr && sourceAttr.trim() ? sourceAttr.trim() : 'cta';
        track('quiz_cta_clicked', { source: source });
        openQuiz({ source: source });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.modalEl.classList.contains('is-open')) closeQuiz();
    });

    // Deep link support: ?openQuiz=1 opens the modal on page load;
    // adding &autostart=1 also skips the intro screen.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('openQuiz') === '1') {
        const autostart = params.get('autostart') === '1';
        params.delete('openQuiz');
        params.delete('autostart');
        const clean = window.location.pathname +
          (params.toString() ? '?' + params.toString() : '') +
          window.location.hash;
        history.replaceState(null, '', clean);
        track('quiz_opened_via_deeplink', { autostart: autostart });
        openQuiz({ source: 'deeplink', autostart: autostart });
      }
    } catch (e) { /* ignore — older browsers */ }
  }

  // ============================================================
  // Context-adaptive PDP badge.
  //
  // Decides which of three states to render based on customer state +
  // current product:
  //   stateA (pre_quiz)     — customer hasn't completed quiz → CTA to take it
  //   stateB (recommended)  — completed + current PDP IS one of their matches
  //   stateC (non_match)    — completed + current PDP is NOT a match
  //
  // The "swim only" toggle (theme-editable) gates State A and State C to
  // swim PDPs (product type metafield = Swimwear or Bodywear) so the
  // badge doesn't pitch the quiz on accessory pages or pester customers
  // on non-swim products. State B always shows (a "matched" product is
  // by definition in the quiz's catalogue).
  // ============================================================
  function getCurrentPdpHandle() {
    // Match /products/X anywhere in the path (handles /en-us/, /collections/X/products/Y, etc.)
    const m = (window.location.pathname || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function isCurrentProductSwim() {
    // If swim-only is off, every PDP is eligible.
    if (PDP_BADGE.swimOnly === false) return true;
    const type = String(CURRENT_PRODUCT && CURRENT_PRODUCT.type || '').trim();
    if (!type) return false;
    const swimTypes = String(PDP_BADGE.swimTypes || '')
      .split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
    return swimTypes.indexOf(type.toLowerCase()) !== -1;
  }

  function determineBadgeState(currentHandle) {
    if (!PDP_BADGE.enabled) return null;
    if (!currentHandle) return null;
    const saved = loadQuizState();
    const hasMatches = !!(saved && saved.matchingProducts && saved.matchingProducts.length);
    if (hasMatches && saved.matchingProducts.indexOf(currentHandle) !== -1) {
      // On a recommended product — always show State B (no swim check needed
      // since it's already in the quiz catalogue).
      return 'recommended';
    }
    // Both pre-quiz (State A) and post-quiz non-match (State C) need the
    // swim-only filter (when enabled).
    if (!isCurrentProductSwim()) return null;
    if (hasMatches) return 'non_match';      // State C
    return 'pre_quiz';                       // State A
  }

  function pdpBadgeFindTarget() {
    const mixMatch = document.querySelector('.mix-match');
    if (mixMatch) return { node: mixMatch, method: 'prepend' };
    const title = document.querySelector('.product__title');
    if (title)   return { node: title,    method: 'before' };
    return null;
  }

  function tryRenderPdpBadge() {
    if (document.querySelector('.quiz-recommended-badge')) return; // idempotent

    const currentHandle = getCurrentPdpHandle();
    const stateName = determineBadgeState(currentHandle);
    if (!stateName) return;

    const target = pdpBadgeFindTarget();
    if (!target) return;

    // Per-state config from theme editor.
    let cfg;
    if (stateName === 'pre_quiz')        cfg = PDP_BADGE.stateA;
    else if (stateName === 'recommended') cfg = PDP_BADGE.stateB;
    else /* non_match */                  cfg = PDP_BADGE.stateC;

    const saved = stateName === 'pre_quiz' ? null : loadQuizState();
    const navigateUrl = saved && saved.url ? String(saved.url).trim() : '';

    // State A → button (no URL, opens modal on click)
    // States B + C → anchor with href = saved quiz URL (navigates back)
    const isLink = stateName !== 'pre_quiz' && navigateUrl;
    const badge = document.createElement(isLink ? 'a' : 'button');
    badge.className = 'quiz-recommended-badge quiz-recommended-badge--' + stateName;
    badge.setAttribute('data-quiz-badge-state', stateName);
    // Inline CSS variables drive the per-state colours from theme editor.
    badge.style.setProperty('--badge-bg',   cfg.bg   || '');
    badge.style.setProperty('--badge-fg',   cfg.text || '');
    if (isLink) {
      badge.href = navigateUrl;
      badge.setAttribute('aria-label', stateName === 'recommended'
        ? 'View all your quiz matches'
        : 'Back to your quiz results');
    } else if (stateName === 'pre_quiz') {
      badge.type = 'button';
      badge.setAttribute('aria-label', cfg.copy || 'Take the fit quiz');
    } else {
      // States B/C with no saved.url — fall back to a static informational
      // chip, no navigation. Rare but possible if state was partially saved.
      badge.classList.add('quiz-recommended-badge--static');
    }

    // ---- Inner HTML per state ----
    let innerHTML = '';
    if (stateName === 'pre_quiz') {
      // State A: simple chip with sparkle + copy. No hover swap.
      innerHTML =
        '<span class="quiz-recommended-badge__default">' +
          '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="M9 2L10 8L16 9L10 10L9 16L8 10L2 9L8 8Z"/>' +
            '<path d="M18 2L18.7 4.3L21 5L18.7 5.7L18 8L17.3 5.7L15 5L17.3 4.3Z"/>' +
            '<path d="M19 15L19.5 16.5L21 17L19.5 17.5L19 19L18.5 17.5L17 17L18.5 16.5Z"/>' +
          '</svg>' +
          '<span class="quiz-recommended-badge__label">' + escapeAttr(cfg.copy || '') + '</span>' +
        '</span>';
    } else if (stateName === 'recommended') {
      // State B: default text + sparkle, hover swaps to back-arrow + hover copy
      innerHTML =
        '<span class="quiz-recommended-badge__default">' +
          '<span class="quiz-recommended-badge__label">' + escapeAttr(cfg.defaultCopy || '') + '</span>' +
          '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="M9 2L10 8L16 9L10 10L9 16L8 10L2 9L8 8Z"/>' +
            '<path d="M18 2L18.7 4.3L21 5L18.7 5.7L18 8L17.3 5.7L15 5L17.3 4.3Z"/>' +
            '<path d="M19 15L19.5 16.5L21 17L19.5 17.5L19 19L18.5 17.5L17 17L18.5 16.5Z"/>' +
          '</svg>' +
        '</span>' +
        (isLink
          ? '<span class="quiz-recommended-badge__hover" aria-hidden="true">' +
              '<span class="quiz-recommended-badge__label">' + escapeAttr(cfg.hoverCopy || '') + '</span>' +
            '</span>'
          : '');
    } else {
      // State C: single-line back-affordance. No hover swap (action is
      // already self-evident — it's a back link).
      innerHTML =
        '<span class="quiz-recommended-badge__default">' +
          '<span class="quiz-recommended-badge__label">' + escapeAttr(cfg.copy || '') + '</span>' +
        '</span>';
    }
    badge.innerHTML = innerHTML;

    // ---- Click behaviour ----
    if (stateName === 'pre_quiz') {
      badge.addEventListener('click', function (e) {
        e.preventDefault();
        track('quiz_pdp_badge_clicked', { state: 'pre_quiz', product_handle: currentHandle });
        openQuiz({ source: 'pdp_badge' });
      });
    } else if (isLink) {
      // Same-URL safety: if the saved URL equals the current location
      // (e.g. customer clicked through to a recommended product from the
      // results page itself), navigate won't fire. Fall back to a soft
      // refresh-style navigation.
      badge.addEventListener('click', function (e) {
        track('quiz_pdp_badge_clicked', { state: stateName, product_handle: currentHandle });
        if (typeof navigateToResults === 'function') {
          e.preventDefault();
          navigateToResults(navigateUrl);
        }
      });
    }

    // ---- Insert into DOM ----
    if (target.method === 'prepend') {
      target.node.insertBefore(badge, target.node.firstChild);
    } else {
      target.node.parentNode.insertBefore(badge, target.node);
    }
  }

  // Back-compat alias so existing call sites keep working.
  function tryRenderRecommendedBadge() { return tryRenderPdpBadge(); }

  // Mix-match PDPs swap products via history.pushState — pathname changes
  // without a page reload. Re-evaluate the badge on URL changes so it
  // appears, disappears, or re-renders for the new product.
  function setupBadgeUrlWatcher() {
    let lastPath = window.location.pathname;
    function check() {
      if (window.location.pathname === lastPath) return;
      lastPath = window.location.pathname;
      const existing = document.querySelector('.quiz-recommended-badge');
      if (existing) existing.remove();
      // Small delay lets the swap's DOM operations (gallery, title etc.) settle.
      setTimeout(tryRenderRecommendedBadge, 80);
    }
    ['pushState', 'replaceState'].forEach(function (fnName) {
      const orig = history[fnName];
      history[fnName] = function () {
        const ret = orig.apply(history, arguments);
        check();
        return ret;
      };
    });
    window.addEventListener('popstate', check);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      tryRenderRecommendedBadge();
      setupBadgeUrlWatcher();
    });
  } else {
    init();
    tryRenderRecommendedBadge();
    setupBadgeUrlWatcher();
  }
})();
